import React, { useState, useMemo, useEffect, useLayoutEffect, useRef } from 'react';
import {
  Box,
  TextField,
  Button,
  Paper,
  Typography,
  Grid,
  IconButton,
  Container,
  Divider,
  FormControl,
  FormControlLabel,
  Checkbox,
  FormHelperText,
  InputLabel,
  Select,
  MenuItem,
  ToggleButtonGroup,
  ToggleButton,
  Chip,
  Tooltip,
  Popover,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
} from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';

import { useAuth } from '../../contexts/AuthContext';
import fittingsData from '../fittingsData';
import {
  findStraightCap,
  CIRCUIT_FITTING_CATEGORIES,
  getAvailableDiameters,
  isEndFittingCompatibleWithMainPipe,
  isDistributionCircuitFittingCompatibleWithSquareProfile,
  isDistributionCircuitFittingCompatibleWithRoundPipe,
  isDistributionEndFittingCompatibleWithSquareProfile as isEndFittingCompatibleWithSquareProfile,
  getMaxThermalPowerKwCollectorCharacteristics,
  getMaxThermalPowerKwSeparatorSquareCorpus,
  isRoundCollectorPipeCompatibleWithSeparator,
  isSquareCollectorProfileCompatibleWithSeparator,
  THERMAL_POWER_INPUT_MIN_KW,
  pickLargestCompatibleFittingIdInCategory,
} from './collectorUtils';
import {
  isFittingCompatibleWithMainPipe,
  isFittingCompatibleWithSquareProfile,
} from './separatorFittingCompatibility';
import { getCapUpdatesForConnectionFitting } from './endConnectionsLogic';
import { getPipeDiameterForFitting } from './connectionLogic';
import {
  getCollectorWithSeparatorFirstLastOffsetsMm,
  computeDistributionCircuitSpacingLayout,
} from './distributionCollectorOffsets';
import {
  HYDRAULIC_SEPARATOR_CLIENT_POWER_STEPS,
  HYDRAULIC_SEPARATOR_CLIENT_POWER_STEPS_MAX,
  HYDRAULIC_SEPARATOR_CLIENT_PARAMS_TABLE,
} from './hydraulicSeparatorClientParams';
import {
  DISTRIBUTION_COLLECTOR_CLIENT_PARAMS_TABLE,
  isDistributionClientPresetSquareVelocityException,
  resolvePresetPressure,
} from './distributionCollectorClientParams';
import {
  CWS_HYDRAULIC_SEP_CLIENT_OFFSET_MAX_PRESET_KW,
  CWS_HYDRAULIC_SEP_CLIENT_OFFSETS_BY_PRESET_KW,
  getCwsCollectorPipeSpacingMm,
} from './collectorWithSeparatorHydraulicClientOffsets';
import {
  ALL_SEPARATOR_DIAMETERS,
  ALL_SEPARATOR_SQUARE_PROFILES,
  CLIENT_OVER_TABLE_SEP_CORPUS_TARGET_VELOCITY_MS,
  collectorInteraxisFromSeparatorConnectionSpacingMm,
  roundUp5mm,
  separatorLateralStepMmFromConnAndSepInner,
  pickOptimalSeparatorCorpusRowForClientFlow,
} from './collectorSeparatorClientGeometry';
import {
  CLIENT_THERMAL_DELTA_TEMP_CHOICES,
  clientUsesPresetDeltaTemp,
  CLIENT_STANDARD_PRODUCTS_DELTA_TEMP_LABEL,
  getCwsPresetReferenceDeltaTemp,
  normalizeClientThermalDeltaTemp,
} from './clientPresetReferenceDelta';
import {
  CLIENT_STANDARD_PRODUCTS_CONNECTION_LABEL,
  clientUsesPresetCollectorSize,
  clientUsesPresetConnectionType,
  clientUsesCatalogPresetConnection,
  getHydraulicSeparatorPresetConnectionType,
  getHydraulicSeparatorPresetConnectionSize,
} from './clientPresetConnection';
import {
  getHydraulicSeparatorClientMaxTableFlowM3h,
  isClientFlowM3hOverCatalogTable,
} from './clientPresetCatalogFlow';
import {
  clientCatalogPresetKeyCeilByTableFlowM3h,
  resolveClientConstructionFromThermalInput,
} from './clientConstructionPreset';

function cwsClientStepOk(stepKw, shape) {
  const hp = HYDRAULIC_SEPARATOR_CLIENT_PARAMS_TABLE[stepKw];
  const dp = DISTRIBUTION_COLLECTOR_CLIENT_PARAMS_TABLE[stepKw];
  if (!hp || !dp) return false;
  if (shape === 'square' && (!hp.squareProfile || !dp.squareProfile)) return false;
  return true;
}

function syncCwsClientThermalFlowFromPower(next) {
  const p = Number(next.power);
  if (!Number.isFinite(p) || p <= 0) return;
  const q = calculateFlowRateFromPower(p, normalizeClientThermalDeltaTemp(next.deltaTemp));
  if (!Number.isFinite(q) || q <= 0) return;
  next.flowRate = parseFloat(q.toFixed(2));
}

// ─────────────────────────────────────────────────────────────────────────────
// Константы и хелперы для гидроразделителя (взяты из CharacteristicsCalculator)
// ─────────────────────────────────────────────────────────────────────────────

const COLLECTOR_SQUARE_PROFILES = [
  { value: '40x40',   label: '40×40',   side: 40 },
  { value: '50x50',   label: '50×50',   side: 50 },
  { value: '60x60',   label: '60×60',   side: 60 },
  { value: '80x80',   label: '80×80',   side: 80 },
  { value: '100x100', label: '100×100', side: 100 },
  { value: '150x150', label: '150×150', side: 150 },
];

// Типы боковых подключений гидроразделителя
const SEP_CONNECTION_TYPES = [
  { id: 'nipple',  label: 'Ниппели' },
  { id: 'coupling',label: 'Муфты' },
  { id: 'flange',  label: 'Фланцы' },
  { id: 'nut',     label: 'Накидные гайки' },
  { id: 'washer',  label: 'Шайбы с нар. резьбой' },
];

// Размеры подключений (дублировано из CharacteristicsCalculator)
const SEP_CONNECTION_SIZES = [
  { diameter: 13.7, displayName: '1/4"' },
  { diameter: 17.2, displayName: '3/8"' },
  { diameter: 21.3, displayName: '1/2"' },
  { diameter: 26.9, displayName: '3/4"' },
  { diameter: 33.7, displayName: '1"' },
  { diameter: 42.4, displayName: '1 1/4"' },
  { diameter: 48.3, displayName: '1 1/2"' },
  { diameter: 60.3, displayName: '2"' },
  { diameter: 76.1, displayName: '2 1/2"' },
  { diameter: 88.9, displayName: '3"' },
  { diameter: 114.3,displayName: '4"' },
  { diameter: 25,   displayName: 'Ду25',  isFlangeSize: true, nominalDiameter: 25 },
  { diameter: 32,   displayName: 'Ду32',  isFlangeSize: true, nominalDiameter: 32 },
  { diameter: 40,   displayName: 'Ду40',  isFlangeSize: true, nominalDiameter: 40 },
  { diameter: 50,   displayName: 'Ду50',  isFlangeSize: true, nominalDiameter: 50 },
  { diameter: 65,   displayName: 'Ду65',  isFlangeSize: true, nominalDiameter: 65 },
  { diameter: 80,   displayName: 'Ду80',  isFlangeSize: true, nominalDiameter: 80 },
  { diameter: 100,  displayName: 'Ду100', isFlangeSize: true, nominalDiameter: 100 },
  { diameter: 125,  displayName: 'Ду125', isFlangeSize: true, nominalDiameter: 125 },
  { diameter: 150,  displayName: 'Ду150', isFlangeSize: true, nominalDiameter: 150 },
  { diameter: 200,  displayName: 'Ду200', isFlangeSize: true, nominalDiameter: 200 },
  { diameter: 250,  displayName: 'Ду250', isFlangeSize: true, nominalDiameter: 250 },
  { diameter: 300,  displayName: 'Ду300', isFlangeSize: true, nominalDiameter: 300 },
  { diameter: 350,  displayName: 'Ду350', isFlangeSize: true, nominalDiameter: 350 },
];

// ─────────────────────────────────────────────────────────────────────────────
// Утилиты гидроразделителя
// ─────────────────────────────────────────────────────────────────────────────

function calculateFlowRateFromPower(power, deltaTemp) {
  if (!power || !deltaTemp) return 0;
  const flowRateM3s = (power * 1000) / (1000 * 4190 * deltaTemp);
  return flowRateM3s * 3600;
}

function calculatePowerFromFlowRate(flowRate, deltaTemp) {
  if (!flowRate || !deltaTemp) return 0;
  const flowRateM3s = flowRate / 3600;
  return (flowRateM3s * 1000 * 4190 * deltaTemp) / 1000;
}

function calculateVelocity(flowRate, outerDiameter, wallThickness = 2) {
  const flowRateM3s = flowRate / 3600;
  const innerDiameter = outerDiameter - 2 * wallThickness;
  const area = Math.PI * Math.pow(innerDiameter / 1000 / 2, 2);
  return area > 0 ? flowRateM3s / area : 0;
}

function parseSeparatorDiameterKey(key) {
  if (!key) return { od: null, wt: null };
  const parts = String(key).split('-');
  const od = parseFloat(parts[0]);
  const wt = parseInt(parts[1]);
  return { od: Number.isNaN(od) ? null : od, wt: Number.isNaN(wt) ? null : wt };
}

/** Гидрострелка: прямые заглушки в изделии до ⌀ 101.6 мм включительно; выше — только эллиптические (8/10 бар). */
const SEPARATOR_STRAIGHT_CAP_MAX_OUTER_MM = 101.6;

/** Круглая гидрострелка: режим 6/8 бар (прямая заглушка) недоступен. */
function roundSeparatorDisallowsSixEightBar(outerMm) {
  if (outerMm == null || Number.isNaN(outerMm)) return false;
  if (outerMm > SEPARATOR_STRAIGHT_CAP_MAX_OUTER_MM) return true;
  return !findStraightCap(outerMm);
}

/** Круглый распределительный коллектор — как в CollectorCharacteristicsCalculator. */
function roundCollectorDisallowsSixEightBar(outerMm) {
  if (outerMm == null || Number.isNaN(outerMm)) return false;
  return outerMm > 60.3 || !findStraightCap(outerMm);
}

function getSepConnectionWallThickness(connectionType) {
  if (connectionType === 'nipple' || connectionType === 'coupling') return 3;
  return 2;
}

function getSepTransitionPipeDiameter(connectionType, size) {
  if (connectionType === 'flange') {
    const DN_TO_PIPE = { 25:33.7,32:42.4,40:48.3,50:60.3,65:76.1,80:88.9,100:114.3,125:139.7,150:168.3,200:219.1,250:273,300:323.9,350:355.6 };
    return DN_TO_PIPE[size] || size;
  }
  if (connectionType === 'nut') {
    const MAP = { 33.7:21.3,42.4:32,48.3:32,60.3:42.4 };
    return MAP[size] || size;
  }
  if (connectionType === 'washer') {
    const MAP = { 33.7:26.9,42.4:32,48.3:32,60.3:42.4 };
    return MAP[size] || size;
  }
  return size;
}

function getSepFittingIdFromConnection(connectionType, connectionSize) {
  if (!connectionSize) return 'Nippel_1';
  const sizeToSuffix = {
    13.7:'1.4',17.2:'3.8',21.3:'1.2',26.9:'3.4',33.7:'1',
    42.4:'1_1.4',48.3:'1_1.2',60.3:'2',76.1:'2_1/2',88.9:'3',114.3:'4',
  };
  if (connectionType === 'flange') return `Flanec_${connectionSize}_galv`;
  const suffix = sizeToSuffix[connectionSize];
  if (!suffix) return 'Nippel_1';
  if (connectionType === 'nipple')   return `Nippel_${suffix}`;
  if (connectionType === 'coupling') return `Mufta_${suffix}`;
  if (connectionType === 'nut')      return `Nak_Gaika_${suffix}`;
  if (connectionType === 'washer')   return `Shaib_Nar_Rezba_${suffix}`;
  return 'Nippel_1';
}

function getMaxSepConnectionDiameter(mainDiameter, connectionType) {
  if (!mainDiameter) return 0;
  const t = connectionType;
  if (mainDiameter <= 42.4) {
    if (t === 'nipple')  return 26.9;
    if (t === 'coupling')return 21.3;
    if (t === 'flange')  return 0;
    return 33.7;
  }
  if (mainDiameter <= 48.3) {
    if (t === 'nipple')  return 26.9;
    if (t === 'coupling')return 26.9;
    if (t === 'flange')  return 0;
    return 33.7;
  }
  if (mainDiameter <= 60.3) {
    if (t === 'nipple')  return 33.7;
    if (t === 'coupling')return 33.7;
    if (t === 'flange')  return 25;
    return 48.3;
  }
  if (mainDiameter <= 76.1) {
    if (t === 'nipple')  return 48.3;
    if (t === 'coupling')return 42.4;
    if (t === 'flange')  return 40;
    return 60.3;
  }
  if (mainDiameter <= 88.9) {
    if (t === 'nipple')  return 60.3;
    if (t === 'coupling')return 48.3;
    if (t === 'flange')  return 50;
    return 60.3;
  }
  if (mainDiameter <= 114.3) {
    if (t === 'nipple')  return 76.1;
    if (t === 'coupling')return 60.3;
    if (t === 'flange')  return 65;
    return 76.1;
  }
  if (mainDiameter <= 139.7) {
    if (t === 'nipple')  return 88.9;
    if (t === 'coupling')return 60.3;
    if (t === 'flange')  return 80;
    return 88.9;
  }
  if (mainDiameter <= 168.3) {
    if (t === 'nipple')  return 114.3;
    if (t === 'coupling')return 60.3;
    if (t === 'flange')  return 100;
    return 114.3;
  }
  if (t === 'flange') return 350;
  return 114.3;
}

/** Порядок автоподбора категории «Подключение источника», если для выбранной нет размеров в 0,3–1,5 м/с. */
const SEP_CONNECTION_TYPE_AUTO_FALLBACK_ORDER = ['flange', 'coupling', 'nipple', 'nut', 'washer'];

function filterSepConnectionSizesForType(q, ct, mainOd, effectiveSepProfile, isSquare) {
  const maxDia = mainOd ? getMaxSepConnectionDiameter(mainOd, ct) : Infinity;
  const wt = getSepConnectionWallThickness(ct);

  return SEP_CONNECTION_SIZES.filter(({ diameter, isFlangeSize, nominalDiameter }) => {
    if (ct === 'flange' && !isFlangeSize) return false;
    if (ct !== 'flange' && isFlangeSize) return false;
    if (ct === 'coupling' && diameter > 60.3) return false;
    if (ct === 'nut' && ![33.7, 42.4, 48.3, 60.3].includes(diameter)) return false;
    if (ct === 'washer' && ![33.7, 42.4, 48.3, 60.3].includes(diameter)) return false;
    if (ct === 'nipple' && diameter > 114.3) return false;

    const dToCompare = ct === 'flange' ? nominalDiameter : diameter;
    if (dToCompare > maxDia) return false;

    const fittingId = getSepFittingIdFromConnection(ct, ct === 'flange' ? nominalDiameter : diameter);
    if (!isSquare && mainOd && !isFittingCompatibleWithMainPipe(fittingId, mainOd)) return false;
    if (isSquare && effectiveSepProfile && !isFittingCompatibleWithSquareProfile(fittingId, effectiveSepProfile)) {
      return false;
    }

    const effD = getSepTransitionPipeDiameter(ct, ct === 'flange' ? nominalDiameter : diameter);
    const v = calculateVelocity(q, effD, wt);
    return v >= 0.3 && v <= 1.5;
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Константы и хелперы для контуров коллектора (из CollectorCharacteristicsCalculator)
// ─────────────────────────────────────────────────────────────────────────────

const DEFAULT_FITTING_CATEGORY = 'Ниппели';
const DEFAULT_FITTING_ID       = 'Nippel_1';

/** Как в CollectorCharacteristicsCalculator / CharacteristicsCalculator — только фиксированные шаги ΔT */
const ALLOWED_DELTA_TEMPS = [5, 10, 15, 20, 25];
function normalizeDeltaTemp(dt) {
  const n = Number(dt);
  if (ALLOWED_DELTA_TEMPS.includes(n)) return n;
  if (!Number.isFinite(n)) return 25;
  return ALLOWED_DELTA_TEMPS.reduce((best, v) =>
    (Math.abs(v - n) < Math.abs(best - n) ? v : best), 25);
}

const CWS_CLIENT_POWER_MAX_KW = HYDRAULIC_SEPARATOR_CLIENT_POWER_STEPS_MAX;

function resolveCwsClientConstructionContext(state) {
  const maxTableQ = getHydraulicSeparatorClientMaxTableFlowM3h(state.shape);
  return resolveClientConstructionFromThermalInput({
    inputMode: state.inputMode,
    powerKw: state.power,
    flowRateM3h: state.flowRate,
    deltaTemp: state.deltaTemp,
    shape: state.shape,
    paramsTable: HYDRAULIC_SEPARATOR_CLIENT_PARAMS_TABLE,
    powerSteps: HYDRAULIC_SEPARATOR_CLIENT_POWER_STEPS,
    maxStepKw: CWS_CLIENT_POWER_MAX_KW,
    maxTableFlowM3h: maxTableQ,
    calcFlowFromPowerKw: (powerKw, dt) => calculateFlowRateFromPower(powerKw, dt),
    extraStepOk: (stepKw) => cwsClientStepOk(stepKw, state.shape),
  });
}

/** ΔT по умолчанию для инженеров. */
const CWS_ENGINEERS_DEFAULT_DELTA_TEMP_KW = 25;
/** Клиент, мощность свыше таблицы: подбор по скоростям в гребёнке / боковом фланце (корпус стрелки — CLIENT_OVER_TABLE_SEP_CORPUS_TARGET_VELOCITY_MS) */
const CWS_CLIENT_OVER_TABLE_COLLECTOR_TARGET_VELOCITY_MS = 0.6;
const CWS_CLIENT_OVER_TABLE_SOURCE_CONNECTION_TARGET_VELOCITY_MS = 1.0;
const CWS_VIEW_MODE_STORAGE_KEY = 'cwsCharacteristicsViewMode';

function clampCwsClientPowerKw(raw, maxP) {
  return Math.min(Math.max(raw, THERMAL_POWER_INPUT_MIN_KW), maxP);
}

/**
 * Ключ табличного пресета (кВт) по расходу м³/ч.
 * Сначала совпадение с табличным flowRate (таблица не совпадает с P=Q·c·ΔT из-за округлений).
 * Иначе — оценка мощности из Q и ΔT и шаг вверх (свыше таблицы — null).
 */
function clientPresetKeyFromFlowM3h(flowNum, _deltaTemp, shape) {
  if (!Number.isFinite(flowNum) || flowNum <= 0) return null;
  const maxTableQ = getHydraulicSeparatorClientMaxTableFlowM3h(shape);
  if (isClientFlowM3hOverCatalogTable(flowNum, maxTableQ)) return null;
  return clientCatalogPresetKeyCeilByTableFlowM3h(
    flowNum,
    shape,
    HYDRAULIC_SEPARATOR_CLIENT_PARAMS_TABLE,
    HYDRAULIC_SEPARATOR_CLIENT_POWER_STEPS,
    (stepKw) => cwsClientStepOk(stepKw, shape)
  );
}

function resolveCwsClientPresetKeyFromState(state) {
  const ctx = resolveCwsClientConstructionContext(state);
  if (!ctx || ctx.overTable) return null;
  const key = ctx.constructionKeyKw;
  if (key == null) return null;
  if (!cwsClientStepOk(key, state.shape)) return null;
  return key;
}

function applyCwsClientDeltaTempFromConstructionPreset(next) {
  if (!clientUsesPresetDeltaTemp(next)) return;
  const key = resolveCwsClientPresetKeyFromState(next);
  if (key == null) return;
  next.deltaTemp = getCwsPresetReferenceDeltaTemp(key);
}

function seedCwsClientConnectionFromCatalog(next) {
  const key = resolveCwsClientPresetKeyFromState(next);
  if (key == null) return;
  const connectionType = getHydraulicSeparatorPresetConnectionType(key);
  next.connectionType = connectionType;
  const sz = getHydraulicSeparatorPresetConnectionSize(key, connectionType);
  if (sz != null) next.connectionSize = sz;
}

function applyCwsClientConnectionFromConstructionPreset(next) {
  if (!clientUsesPresetConnectionType(next)) return;
  seedCwsClientConnectionFromCatalog(next);
}

function seedCwsClientCollectorFromCatalog(next) {
  const key = resolveCwsClientPresetKeyFromState(next);
  if (key == null) return;
  const dp = DISTRIBUTION_COLLECTOR_CLIENT_PARAMS_TABLE[key];
  if (!dp) return;
  if (next.shape === 'square') {
    if (dp.squareProfile) next.collectorProfile = dp.squareProfile;
  } else if (dp.diameter != null) {
    next.collectorDiameter = dp.diameter;
  }
}

function applyCwsClientCollectorFromConstructionPreset(next) {
  if (!clientUsesPresetCollectorSize(next)) return;
  seedCwsClientCollectorFromCatalog(next);
}

function applyCwsClientPowerPresetSnap(next, clampedPowerKw) {
  if (!Number.isFinite(clampedPowerKw) || clampedPowerKw <= 0) {
    next.power = clampedPowerKw;
    return;
  }
  const qSnap = calculateFlowRateFromPower(clampedPowerKw, next.deltaTemp);
  const maxTableQ = getHydraulicSeparatorClientMaxTableFlowM3h(next.shape);
  if (isClientFlowM3hOverCatalogTable(qSnap, maxTableQ)) {
    next.power = clampedPowerKw;
    next.flowRate = parseFloat(qSnap.toFixed(2));
    return;
  }
  next.power = clampCwsClientPowerKw(clampedPowerKw, CWS_CLIENT_POWER_MAX_KW);
  syncCwsClientThermalFlowFromPower(next);
  applyCwsClientDeltaTempFromConstructionPreset(next);
  applyCwsClientConnectionFromConstructionPreset(next);
  applyCwsClientCollectorFromConstructionPreset(next);
}

function initialCwsCalculatorViewMode(initialParams) {
  const vm = initialParams?._calculatorViewMode;
  if (vm === 'clients' || vm === 'engineers') return vm;
  try {
    const s = localStorage.getItem(CWS_VIEW_MODE_STORAGE_KEY);
    if (s === 'clients' || s === 'engineers') return s;
  } catch (_) {}
  return 'clients';
}

/** Подписи для режима «только чтение» по строкам таблицы клиента */
function formatClientPresetSeparatorLabel(hp, shapeIsSquare) {
  if (!hp) return '—';
  if (shapeIsSquare && hp.squareProfile) {
    const wt = hp.squareWallThickness ?? 2;
    const side = parseInt(hp.squareProfile.split('x')[0], 10);
    const inner = Number.isFinite(side) ? side - 2 * wt : '—';
    return `${hp.squareProfile.replace('x', '×')} ×${wt} (внутр. ${inner}×${inner})`;
  }
  if (hp.diameter != null && hp.wallThickness != null) {
    const inner = hp.diameter - 2 * hp.wallThickness;
    return `⌀${hp.diameter} ×${hp.wallThickness} (внутр. ⌀${inner})`;
  }
  return '—';
}

function formatClientPresetCollectorLabel(dp, shapeIsSquare) {
  if (!dp) return '—';
  if (shapeIsSquare && dp.squareProfile) {
    const side = parseInt(dp.squareProfile.split('x')[0], 10);
    const inner = Number.isFinite(side) ? side - 4 : '—';
    return `${dp.squareProfile.replace('x', '×')} (внутр. ${inner}×${inner})`;
  }
  if (dp.diameter != null) {
    const wt = 2;
    const inner = dp.diameter - 2 * wt;
    return `⌀${dp.diameter} (внутр. ⌀${inner})`;
  }
  return '—';
}

/** Подпись корпуса стрелки для режима автоподбора (ключ или строка optimalSepDiameter) */
function formatClientAutoSeparatorLabelFromKey(key, shapeIsSquare, optimalFallback) {
  const k = key ?? (optimalFallback
    ? (shapeIsSquare
      ? `${optimalFallback.profile}-${optimalFallback.wallThickness}`
      : `${optimalFallback.outerDiameter}-${optimalFallback.wallThickness}`)
    : null);
  if (!k) return '—';
  const lastDash = k.lastIndexOf('-');
  if (lastDash <= 0) return '—';
  const a = k.slice(0, lastDash);
  const wt = Number(k.slice(lastDash + 1));
  if (shapeIsSquare) {
    const side = parseInt(a.split('x')[0], 10);
    const inner = Number.isFinite(side) && Number.isFinite(wt) ? side - 2 * wt : '—';
    return `${a.replace('x', '×')} ×${wt} (внутр. ${inner}×${inner})`;
  }
  const od = Number(a);
  const inner = Number.isFinite(od) && Number.isFinite(wt) ? od - 2 * wt : '—';
  return `⌀${od} ×${wt} (внутр. ⌀${inner})`;
}

const shortFittingLabel = (displayName) => {
  if (!displayName) return '—';
  const du = displayName.match(/Ду\s*(\d+)/);
  if (du) return `Ду${du[1]}`;
  const inch = displayName.match(/^[А-Яа-яA-Za-z\s]+?(\d[\d\s/]+")/);
  if (inch) {
    return inch[1].trim()
      .replace('1/4', '¼').replace('3/4', '¾')
      .replace('1/2', '½').replace('3/8', '⅜');
  }
  return displayName;
};

function getFittingsByCategory(category, compatibilityFn) {
  return Object.entries(fittingsData)
    .filter(([, d]) => d.category === category)
    .map(([id, d]) => ({
      id,
      label:      d.displayName ?? id,
      shortLabel: shortFittingLabel(d.displayName ?? id),
      diameter:   d.diameter ?? 0,
      isCompatible: compatibilityFn ? compatibilityFn(id) : true,
    }))
    .sort((a, b) => a.diameter - b.diameter);
}

function clampCircuitsToCompatibility(circuits, isCompatible) {
  return circuits.map((c) => {
    if (!c.fitting || isCompatible(c.fitting)) return c;
    const cat = c.fittingCategory ?? DEFAULT_FITTING_CATEGORY;
    const best = pickLargestCompatibleFittingIdInCategory(cat, isCompatible);
    const nextId = best ?? c.fitting;
    return { ...c, fitting: nextId };
  });
}

/** Правый торец — те же правила, что торцевое подключение (isEndFitting*), категория из слота. */
function clampRightEndCircuitToCompatibility(end, endCompat) {
  if (!end?.fitting) return end;
  const cat = end.fittingCategory ?? DEFAULT_FITTING_CATEGORY;
  const catalogCategory = fittingsData[end.fitting]?.category;
  const categoryMismatch = catalogCategory != null && catalogCategory !== cat;
  if (endCompat(end.fitting) && !categoryMismatch) return end;
  const best = pickLargestCompatibleFittingIdInCategory(cat, endCompat);
  const nextId = best ?? end.fitting;
  return { ...end, fitting: nextId, fittingCategory: cat };
}

let _circuitIdCounter = 0;
const newCircuitId = () => `cwsc-${++_circuitIdCounter}`;

// ─────────────────────────────────────────────────────────────────────────────
// Компонент
// ─────────────────────────────────────────────────────────────────────────────

function CollectorWithSeparatorCharacteristicsCalculator({
  onBack,
  onCalculate,
  onBackToDrawing,
  initialParams,
}) {
  const { isAdmin } = useAuth();
  const lastInitialParamsRef = useRef(null);
  const shouldSaveRef = useRef(true);
  const preservedCwsEngineersDeltaRef = useRef(null);
  const [viewMode, setViewMode] = useState(() => initialCwsCalculatorViewMode(initialParams));

  // ─── Состояние: общие характеристики ────────────────────────────────────
  const [characteristics, setCharacteristics] = useState(() => {
    if (initialParams?._characteristics) {
      lastInitialParamsRef.current = initialParams;
      shouldSaveRef.current = false;
      const c = initialParams._characteristics;
      return {
        ...c,
        deltaTemp: normalizeDeltaTemp(c.deltaTemp),
        clientPresetDeltaTemp:
          c.clientPresetDeltaTemp !== undefined
            ? Boolean(c.clientPresetDeltaTemp)
            : !Boolean(c.clientManualDeltaTemp),
        clientPresetConnectionType:
          c.clientPresetConnectionType !== undefined ? Boolean(c.clientPresetConnectionType) : true,
        clientPresetCollectorSize:
          c.clientPresetCollectorSize !== undefined ? Boolean(c.clientPresetCollectorSize) : true,
      };
    }
    try {
      const saved = localStorage.getItem('cwsCharacteristics');
      if (saved) {
        const p = JSON.parse(saved);
        if (p.inputMode && p.deltaTemp != null) {
          return {
            ...p,
            deltaTemp: normalizeDeltaTemp(p.deltaTemp),
            clientPresetDeltaTemp:
              p.clientPresetDeltaTemp !== undefined
                ? Boolean(p.clientPresetDeltaTemp)
                : !Boolean(p.clientManualDeltaTemp),
            clientPresetConnectionType:
              p.clientPresetConnectionType !== undefined ? Boolean(p.clientPresetConnectionType) : true,
            clientPresetCollectorSize:
              p.clientPresetCollectorSize !== undefined ? Boolean(p.clientPresetCollectorSize) : true,
          };
        }
      }
    } catch (e) { /* ignore */ }
    const startKw = 85;
    const startRow = HYDRAULIC_SEPARATOR_CLIENT_PARAMS_TABLE[startKw];
    return {
      inputMode: 'power',
      power: startKw,
      flowRate: parseFloat(Number(startRow?.flowRate ?? 3).toFixed(2)),
      deltaTemp: getCwsPresetReferenceDeltaTemp(startKw),
      clientPresetDeltaTemp: true,
      clientPresetConnectionType: true,
      clientPresetCollectorSize: true,
      shape: 'round',
      pressure: 6,
      // ── Гидроразделитель ──
      separatorDiameter: null,
      connectionType: 'nipple',
      connectionSize: null,
      connectionsCount: 4,
      // ── Коллектор ──
      collectorDiameter: null,
      collectorProfile: null,
    };
  });

  /** Зафиксированные мощность/расход для подразмеров коллектора (гребёнка), без подстройки на каждый символ. */
  const [committedThermal, setCommittedThermal] = useState(null);

  useLayoutEffect(() => {
    setCommittedThermal((prev) =>
      prev != null ? prev : { power: characteristics.power, flowRate: characteristics.flowRate }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!shouldSaveRef.current) { shouldSaveRef.current = true; return; }
    try { localStorage.setItem('cwsCharacteristics', JSON.stringify(characteristics)); } catch (e) { /* ignore */ }
  }, [characteristics]);

  useEffect(() => {
    try {
      localStorage.setItem(CWS_VIEW_MODE_STORAGE_KEY, viewMode);
    } catch (_) {}
  }, [viewMode]);

  useEffect(() => {
    if (viewMode !== 'engineers') return;
    const saved = preservedCwsEngineersDeltaRef.current;
    const nextDt =
      saved != null && ALLOWED_DELTA_TEMPS.includes(saved)
        ? saved
        : CWS_ENGINEERS_DEFAULT_DELTA_TEMP_KW;
    setCharacteristics((prev) => {
      if (Number(prev.deltaTemp) === nextDt) return prev;
      return { ...prev, deltaTemp: nextDt };
    });
  }, [viewMode]);

  // Клиенты + квадрат: мощность не выше 600 кВт (как у отдельного калькулятора стрелки)
  useEffect(() => {
    if (viewMode !== 'clients' || characteristics.shape !== 'square' || characteristics.inputMode !== 'power') return;
    const lim = 600;
    const p = characteristics.power;
    if (typeof p === 'number' && !Number.isNaN(p) && p > lim) {
      setCharacteristics((prev) => ({ ...prev, power: lim }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode]);

  // ─── Контуры ─────────────────────────────────────────────────────────────
  const makeDefaultCircuit = (saved) => ({
    id: newCircuitId(),
    fittingCategory: saved?.fittingCategory ?? DEFAULT_FITTING_CATEGORY,
    fitting:         saved?.fitting         ?? DEFAULT_FITTING_ID,
  });

  const initCircuits = (savedList, count) => {
    if (savedList?.length > 0) return savedList.map(makeDefaultCircuit);
    return Array.from({ length: count ?? 3 }, () => makeDefaultCircuit());
  };

  const [supplyCircuits, setSupplyCircuits] = useState(() =>
    initCircuits(initialParams?._characteristics?.supplyCircuits, 3)
  );
  const [returnCircuits, setReturnCircuits] = useState(() =>
    initCircuits(initialParams?._characteristics?.returnCircuits, 3)
  );

  // Торцевые контуры коллектора: null = нет, объект = выбран фитинг
  const makeDefaultEndCircuit = (saved) => saved
    ? { fittingCategory: saved.fittingCategory ?? DEFAULT_FITTING_CATEGORY, fitting: saved.fitting ?? DEFAULT_FITTING_ID }
    : null;

  /** Торцевой контур только справа: слева гребёнки впритык к разделителю (см. collectorWithSeparatorLogic). */
  const [rightEndCircuit, setRightEndCircuit] = useState(() =>
    makeDefaultEndCircuit(initialParams?._characteristics?.rightEndCircuit)
  );

  // Синхронизируем end circuits при возврате из редактора
  useEffect(() => {
    if (initialParams?._characteristics) {
      const newC = initialParams._characteristics;
      const prev = lastInitialParamsRef.current?._characteristics;
      if (!prev || JSON.stringify(prev) !== JSON.stringify(newC)) {
        lastInitialParamsRef.current = initialParams;
        const vm = initialParams._calculatorViewMode;
        if (vm === 'clients' || vm === 'engineers') setViewMode(vm);
        const merged = { ...newC, deltaTemp: normalizeDeltaTemp(newC.deltaTemp) };
        setCharacteristics(merged);
        setCommittedThermal({ power: merged.power, flowRate: merged.flowRate });
        if (newC.supplyCircuits) setSupplyCircuits(newC.supplyCircuits.map(c => ({ ...c, id: newCircuitId() })));
        if (newC.returnCircuits) setReturnCircuits(newC.returnCircuits.map(c => ({ ...c, id: newCircuitId() })));
        setRightEndCircuit(makeDefaultEndCircuit(newC.rightEndCircuit));
      }
    } else if (!initialParams) {
      lastInitialParamsRef.current = null;
    }
  }, [initialParams]); // eslint-disable-line react-hooks/exhaustive-deps

  /* Синхронизация зафиксированных Вт/м³·ч при смене режима формы — не при каждом символе в поле мощности. */
  useEffect(() => {
    setCommittedThermal({
      power: characteristics.power,
      flowRate: characteristics.flowRate,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- см. блок выше
  }, [
    characteristics.shape,
    characteristics.deltaTemp,
    viewMode,
    characteristics.inputMode,
  ]);

  // ─── Popover выбора фитинга ───────────────────────────────────────────────
  const [circuitPopover, setCircuitPopover] = useState(null);

  const openCircuitPopover = (e, row, index = null) => {
    const el = e?.currentTarget ?? e?.target;
    const rect = el?.getBoundingClientRect();
    const anchorPosition = rect
      ? { top: rect.bottom + window.scrollY, left: rect.left + window.scrollX }
      : { top: 0, left: 0 };
    let cat = DEFAULT_FITTING_CATEGORY;
    if (row === 'supply')   cat = supplyCircuits[index]?.fittingCategory ?? DEFAULT_FITTING_CATEGORY;
    if (row === 'return')   cat = returnCircuits[index]?.fittingCategory ?? DEFAULT_FITTING_CATEGORY;
    if (row === 'rightEnd') cat = rightEndCircuit?.fittingCategory ?? DEFAULT_FITTING_CATEGORY;
    setCircuitPopover({ anchorPosition, row, index, step: 'category', category: cat });
  };

  const closeCircuitPopover = () => setCircuitPopover(null);

  const selectCategory = (cat) =>
    setCircuitPopover(prev => ({ ...prev, step: 'fitting', category: cat }));

  const selectFitting = (fittingId) => {
    if (!circuitPopover) return;
    const { row, index, category } = circuitPopover;
    if (row === 'supply') {
      setSupplyCircuits(prev => prev.map((c, i) => i === index ? { ...c, fittingCategory: category, fitting: fittingId } : c));
    } else if (row === 'return') {
      setReturnCircuits(prev => prev.map((c, i) => i === index ? { ...c, fittingCategory: category, fitting: fittingId } : c));
    } else if (row === 'rightEnd') {
      setRightEndCircuit({ fittingCategory: category, fitting: fittingId });
    }
    closeCircuitPopover();
  };

  const addCircuit = (row) => {
    const newC = makeDefaultCircuit();
    if (row === 'supply') setSupplyCircuits(prev => [...prev, newC]);
    else setReturnCircuits(prev => [...prev, newC]);
  };
  const removeCircuit = (row, index) => {
    if (row === 'supply') setSupplyCircuits(prev => prev.filter((_, i) => i !== index));
    else setReturnCircuits(prev => prev.filter((_, i) => i !== index));
  };

  // ─── Производные: гидроразделитель ───────────────────────────────────────
  const isSquare = characteristics.shape === 'square';

  const maxClientTableFlowM3h = useMemo(
    () => getHydraulicSeparatorClientMaxTableFlowM3h(characteristics.shape),
    [characteristics.shape]
  );

  const clientThermalFlowM3h = useMemo(() => {
    if (viewMode !== 'clients') return null;
    if (characteristics.inputMode === 'power') {
      const pn =
        typeof characteristics.power === 'number' && !Number.isNaN(characteristics.power)
          ? characteristics.power
          : Number(characteristics.power);
      if (!Number.isFinite(pn) || pn <= 0) return null;
      return calculateFlowRateFromPower(pn, normalizeClientThermalDeltaTemp(characteristics.deltaTemp));
    }
    const qn = Number(characteristics.flowRate);
    return Number.isFinite(qn) && qn > 0 ? qn : null;
  }, [
    viewMode,
    characteristics.inputMode,
    characteristics.power,
    characteristics.flowRate,
    characteristics.deltaTemp,
  ]);

  const isClientOverTablePower = useMemo(() => {
    if (viewMode !== 'clients' || clientThermalFlowM3h == null) return false;
    return isClientFlowM3hOverCatalogTable(clientThermalFlowM3h, maxClientTableFlowM3h);
  }, [viewMode, clientThermalFlowM3h, maxClientTableFlowM3h]);

  const effectiveDeltaTemp = useMemo(() => {
    return normalizeClientThermalDeltaTemp(characteristics.deltaTemp);
  }, [characteristics.deltaTemp]);

  const clientPresetDeltaTempOn = clientUsesPresetDeltaTemp({
    clientPresetDeltaTemp: characteristics.clientPresetDeltaTemp,
    clientManualDeltaTemp: characteristics.clientManualDeltaTemp,
  });

  const clientPresetConnectionTypeOn = clientUsesPresetConnectionType({
    clientPresetConnectionType: characteristics.clientPresetConnectionType,
  });

  const clientPresetCollectorSizeOn = clientUsesPresetCollectorSize({
    clientPresetCollectorSize: characteristics.clientPresetCollectorSize,
  });

  const charForCircuitSizing = useMemo(() => {
    if (committedThermal == null) return characteristics;
    return {
      ...characteristics,
      power:
        characteristics.inputMode === 'power' && committedThermal.power !== undefined
          ? committedThermal.power
          : characteristics.power,
      flowRate:
        characteristics.inputMode === 'flowRate' && committedThermal.flowRate !== undefined
          ? committedThermal.flowRate
          : characteristics.flowRate,
    };
  }, [characteristics, committedThermal]);

  const {
    inputMode: clientInputMode,
    power: clientPower,
    flowRate: clientFlowRate,
    shape: clientShape,
    deltaTemp: clientDeltaTemp,
  } = characteristics;

  const clientConstructionCtx = useMemo(() => {
    if (viewMode !== 'clients') return null;
    return resolveCwsClientConstructionContext({
      inputMode: clientInputMode,
      power: clientPower,
      flowRate: clientFlowRate,
      shape: clientShape,
      deltaTemp: clientDeltaTemp,
    });
  }, [
    viewMode,
    clientInputMode,
    clientPower,
    clientFlowRate,
    clientShape,
    clientDeltaTemp,
  ]);

  const clientPresetPowerKey =
    clientConstructionCtx && !clientConstructionCtx.overTable
      ? clientConstructionCtx.constructionKeyKw
      : null;

  const clientCatalogCollectorPresetOn =
    viewMode === 'clients' &&
    !isClientOverTablePower &&
    clientPresetPowerKey != null &&
    clientPresetCollectorSizeOn;

  const clientCatalogConnectionPresetOn =
    viewMode === 'clients' &&
    clientUsesCatalogPresetConnection(characteristics, clientConstructionCtx);

  const clientConstructionSizingFlowM3h = clientConstructionCtx
    ? clientConstructionCtx.sizingFlowM3h
    : null;

  const clientSizingLockedByPreset = viewMode === 'clients' && clientPresetPowerKey != null;
  /** Галочка «По стандартным изделиям»: ⌀ гребёнки заблокирован и в таблице, и свыше каталога. */
  const clientCollectorFieldLocked =
    viewMode === 'clients' && clientPresetCollectorSizeOn;
  const clientCollectorSizingAutoLocked = clientCollectorFieldLocked;
  /** Табличный пресет стрелки или мощность свыше таблицы: без ручного выбора корпуса стрелки */
  const clientSizingAutoLocked =
    viewMode === 'clients' && (clientSizingLockedByPreset || isClientOverTablePower);

  const clientSepConnectionManualLikeEngineer =
    viewMode === 'clients' && !clientCatalogConnectionPresetOn;

  useEffect(() => {
    if (viewMode !== 'clients') return;
    if (!clientCatalogConnectionPresetOn) return;
    if (clientPresetPowerKey == null) return;
    const t = getHydraulicSeparatorPresetConnectionType(clientPresetPowerKey);
    const sz = getHydraulicSeparatorPresetConnectionSize(clientPresetPowerKey, t);
    setCharacteristics((prev) => {
      if (prev.connectionType === t && (sz == null || prev.connectionSize === sz)) return prev;
      return { ...prev, connectionType: t, ...(sz != null ? { connectionSize: sz } : {}) };
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    viewMode,
    clientCatalogConnectionPresetOn,
    clientPresetPowerKey,
  ]);

  const actualFlowRate = useMemo(() => {
    if (viewMode === 'clients' && clientThermalFlowM3h != null) {
      if (isClientFlowM3hOverCatalogTable(clientThermalFlowM3h, maxClientTableFlowM3h)) {
        return clientThermalFlowM3h;
      }
    }
    if (
      viewMode === 'clients' &&
      clientConstructionSizingFlowM3h != null &&
      Number.isFinite(clientConstructionSizingFlowM3h)
    ) {
      return clientConstructionSizingFlowM3h;
    }
    if (characteristics.inputMode === 'power') {
      const p = characteristics.power === '' ? 0 : Number(characteristics.power);
      const pUse = Number.isFinite(p) && p > 0 ? p : 0;
      return calculateFlowRateFromPower(pUse, effectiveDeltaTemp);
    }
    const q = characteristics.flowRate === '' ? 0 : Number(characteristics.flowRate);
    return Number.isFinite(q) && q > 0 ? q : 0;
  }, [
    viewMode,
    characteristics.inputMode,
    characteristics.power,
    characteristics.flowRate,
    clientConstructionSizingFlowM3h,
    effectiveDeltaTemp,
    clientThermalFlowM3h,
    maxClientTableFlowM3h,
  ]);

  const clientThermalFlowForCircuitsM3h = useMemo(() => {
    if (viewMode !== 'clients') return null;
    if (charForCircuitSizing.inputMode === 'power') {
      const pn =
        typeof charForCircuitSizing.power === 'number' && !Number.isNaN(charForCircuitSizing.power)
          ? charForCircuitSizing.power
          : Number(charForCircuitSizing.power);
      if (!Number.isFinite(pn) || pn <= 0) return null;
      return calculateFlowRateFromPower(
        pn,
        normalizeClientThermalDeltaTemp(charForCircuitSizing.deltaTemp)
      );
    }
    const qn = Number(charForCircuitSizing.flowRate);
    return Number.isFinite(qn) && qn > 0 ? qn : null;
  }, [
    viewMode,
    charForCircuitSizing.inputMode,
    charForCircuitSizing.power,
    charForCircuitSizing.flowRate,
    charForCircuitSizing.deltaTemp,
  ]);

  const isClientOverTablePowerForCircuits = useMemo(() => {
    if (viewMode !== 'clients' || clientThermalFlowForCircuitsM3h == null) return false;
    return isClientFlowM3hOverCatalogTable(
      clientThermalFlowForCircuitsM3h,
      getHydraulicSeparatorClientMaxTableFlowM3h(charForCircuitSizing.shape)
    );
  }, [viewMode, clientThermalFlowForCircuitsM3h, charForCircuitSizing.shape]);

  const clientPresetPowerKeyForCircuits = useMemo(() => {
    if (viewMode !== 'clients') return null;
    const ctx = resolveCwsClientConstructionContext(charForCircuitSizing);
    if (!ctx || ctx.overTable) return null;
    const key = ctx.constructionKeyKw;
    if (key == null || !cwsClientStepOk(key, charForCircuitSizing.shape)) return null;
    return key;
  }, [viewMode, charForCircuitSizing]);

  const actualFlowRateForCircuits = useMemo(() => {
    if (viewMode === 'clients' && clientThermalFlowForCircuitsM3h != null) {
      const maxQ = getHydraulicSeparatorClientMaxTableFlowM3h(charForCircuitSizing.shape);
      if (isClientFlowM3hOverCatalogTable(clientThermalFlowForCircuitsM3h, maxQ)) {
        return clientThermalFlowForCircuitsM3h;
      }
    }
    if (viewMode === 'clients' && clientPresetPowerKeyForCircuits != null) {
      const q = HYDRAULIC_SEPARATOR_CLIENT_PARAMS_TABLE[clientPresetPowerKeyForCircuits]?.flowRate;
      if (q != null) return q;
    }
    if (charForCircuitSizing.inputMode === 'power') {
      const p = charForCircuitSizing.power === '' ? 0 : Number(charForCircuitSizing.power);
      const pUse = Number.isFinite(p) && p > 0 ? p : 0;
      return calculateFlowRateFromPower(pUse, effectiveDeltaTemp);
    }
    const q = charForCircuitSizing.flowRate === '' ? 0 : Number(charForCircuitSizing.flowRate);
    return Number.isFinite(q) && q > 0 ? q : 0;
  }, [
    viewMode,
    charForCircuitSizing.inputMode,
    charForCircuitSizing.power,
    charForCircuitSizing.flowRate,
    charForCircuitSizing.shape,
    clientPresetPowerKeyForCircuits,
    effectiveDeltaTemp,
    clientThermalFlowForCircuitsM3h,
  ]);

  // Фильтрация диаметров разделителя по скорости в корпусе (база без пары гребёнка↔стрелка)
  const filteredSepDiametersBase = useMemo(() => {
    const q = actualFlowRate;
    return ALL_SEPARATOR_DIAMETERS.filter(({ outerDiameter, wallThickness }) => {
      const v = calculateVelocity(q, outerDiameter, wallThickness);
      return v >= 0.09 && v <= 0.25;
    });
  }, [actualFlowRate]);

  const filteredSepProfilesBase = useMemo(() => {
    const q = actualFlowRate;
    return ALL_SEPARATOR_SQUARE_PROFILES.filter(({ profile, wallThickness }) => {
      const side = parseInt(profile.split('x')[0]);
      const inner = side - 2 * wallThickness;
      const area = (inner / 1000) * (inner / 1000);
      const v = area > 0 ? (q / 3600) / area : 0;
      return v >= 0.09 && v <= 0.25;
    });
  }, [actualFlowRate]);

  const explicitRoundCollectorOd =
    !isSquare && characteristics.collectorDiameter != null ? Number(characteristics.collectorDiameter) : null;

  const filteredSepDiameters = useMemo(() => {
    if (explicitRoundCollectorOd == null || Number.isNaN(explicitRoundCollectorOd)) {
      return filteredSepDiametersBase;
    }
    return filteredSepDiametersBase.filter((row) =>
      isRoundCollectorPipeCompatibleWithSeparator(row.outerDiameter, explicitRoundCollectorOd));
  }, [filteredSepDiametersBase, explicitRoundCollectorOd]);

  const explicitSquareCollectorProfile =
    isSquare && characteristics.collectorProfile ? characteristics.collectorProfile : null;

  const filteredSepProfiles = useMemo(() => {
    if (!explicitSquareCollectorProfile) return filteredSepProfilesBase;
    const colSide = parseInt(explicitSquareCollectorProfile.split('x')[0], 10);
    if (!Number.isFinite(colSide)) return filteredSepProfilesBase;
    return filteredSepProfilesBase.filter((row) => {
      const sepSide = parseInt(row.profile.split('x')[0], 10);
      return sepSide >= colSide;
    });
  }, [filteredSepProfilesBase, explicitSquareCollectorProfile]);

  // Авто-подбор корпуса стрелки: до таблицы — ~0,15 м/с; клиент свыше таблицы — ~0,21 м/с.
  const optimalSepDiameter = useMemo(() => {
    const TARGET_V = isClientOverTablePower
      ? CLIENT_OVER_TABLE_SEP_CORPUS_TARGET_VELOCITY_MS
      : 0.15;
    return pickOptimalSeparatorCorpusRowForClientFlow(actualFlowRate, isSquare, TARGET_V);
  }, [actualFlowRate, isSquare, isClientOverTablePower]);

  // Выбранный/авто диаметр разделителя
  const sepDiameterKey = characteristics.separatorDiameter;
  const { od: selectedSepOd, wt: selectedSepWt } = useMemo(
    () => parseSeparatorDiameterKey(sepDiameterKey),
    [sepDiameterKey]
  );
  const effectiveSepOd   = selectedSepOd   ?? optimalSepDiameter?.outerDiameter ?? (optimalSepDiameter ? parseInt(optimalSepDiameter.profile?.split('x')[0]) : null);
  const effectiveSepWt   = selectedSepWt   ?? optimalSepDiameter?.wallThickness ?? 2;
  const effectiveSepProfile = isSquare ? (sepDiameterKey?.split('-')[0] ?? optimalSepDiameter?.profile ?? '80x80') : null;

  /** Корпус стрелки для связки с гребёнкой: по зафиксированному Q — иначе у инженеров живой авто-подбор стрелки сбивает контуры при вводе кВт. */
  const optimalSepDiameterForCircuits = useMemo(() => {
    const TARGET_V = isClientOverTablePowerForCircuits
      ? CLIENT_OVER_TABLE_SEP_CORPUS_TARGET_VELOCITY_MS
      : 0.15;
    return pickOptimalSeparatorCorpusRowForClientFlow(actualFlowRateForCircuits, isSquare, TARGET_V);
  }, [actualFlowRateForCircuits, isSquare, isClientOverTablePowerForCircuits]);

  const effectiveSepOdForCircuits =
    selectedSepOd ??
    optimalSepDiameterForCircuits?.outerDiameter ??
    (optimalSepDiameterForCircuits
      ? parseInt(String(optimalSepDiameterForCircuits.profile).split('x')[0], 10)
      : null);
  const effectiveSepProfileForCircuits = isSquare
    ? (sepDiameterKey?.split('-')[0] ?? optimalSepDiameterForCircuits?.profile ?? '80x80')
    : null;

  // Скорость в корпусе разделителя
  const sepCorpusVelocity = useMemo(() => {
    if (isSquare && effectiveSepProfile) {
      const side = parseInt(effectiveSepProfile.split('x')[0]);
      const inner = side - 2 * effectiveSepWt;
      const area = (inner / 1000) * (inner / 1000);
      return area > 0 ? (actualFlowRate / 3600) / area : null;
    }
    if (!isSquare && effectiveSepOd) {
      return calculateVelocity(actualFlowRate, effectiveSepOd, effectiveSepWt);
    }
    return null;
  }, [isSquare, effectiveSepOd, effectiveSepWt, effectiveSepProfile, actualFlowRate]);

  // Фильтрация размеров боковых подключений разделителя
  const filteredSepConnectionSizes = useMemo(
    () => filterSepConnectionSizesForType(
      actualFlowRate,
      characteristics.connectionType,
      effectiveSepOd,
      effectiveSepProfile,
      isSquare,
    ),
    [actualFlowRate, characteristics.connectionType, effectiveSepOd, effectiveSepProfile, isSquare],
  );

  // Если для выбранной категории нет размеров, переключаем на первую подходящую (часто фланец при большом расходе).
  useEffect(() => {
    if (clientSizingAutoLocked && !clientSepConnectionManualLikeEngineer) return;
    if (!(effectiveSepOd || effectiveSepProfile)) return;
    if (actualFlowRate <= 0) return;
    if (filteredSepConnectionSizes.length > 0) return;

    for (const t of SEP_CONNECTION_TYPE_AUTO_FALLBACK_ORDER) {
      if (t === characteristics.connectionType) continue;
      const list = filterSepConnectionSizesForType(
        actualFlowRate,
        t,
        effectiveSepOd,
        effectiveSepProfile,
        isSquare,
      );
      if (list.length > 0) {
        setCharacteristics((prev) => (
          prev.connectionType === t ? prev : { ...prev, connectionType: t, connectionSize: null }
        ));
        return;
      }
    }
  }, [
    filteredSepConnectionSizes.length,
    actualFlowRate,
    characteristics.connectionType,
    effectiveSepOd,
    effectiveSepProfile,
    isSquare,
    clientSizingAutoLocked,
    clientSepConnectionManualLikeEngineer,
  ]);

  // Авто-обновление размера подключения разделителя
  useEffect(() => {
    if (clientCatalogConnectionPresetOn) {
      return;
    }
    if (clientSizingAutoLocked && !clientSepConnectionManualLikeEngineer) return;
    if (!filteredSepConnectionSizes.length) {
      if (characteristics.connectionSize !== null)
        setCharacteristics(p => ({ ...p, connectionSize: null }));
      return;
    }
    const current = filteredSepConnectionSizes.find(s => s.diameter === characteristics.connectionSize);
    if (current) return;
    const mid = filteredSepConnectionSizes[Math.floor(filteredSepConnectionSizes.length / 2)];
    setCharacteristics(p => ({ ...p, connectionSize: mid.diameter }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredSepConnectionSizes, clientSizingAutoLocked, clientSepConnectionManualLikeEngineer]);

  // Авто-обновление диаметра разделителя
  useEffect(() => {
    if (clientSizingAutoLocked) return;
    if (!optimalSepDiameter) return;
    const optKey = isSquare
      ? `${optimalSepDiameter.profile}-${optimalSepDiameter.wallThickness}`
      : `${optimalSepDiameter.outerDiameter}-${optimalSepDiameter.wallThickness}`;
    const curKey = characteristics.separatorDiameter;
    if (!curKey) return; // авто — не трогаем пока null
    // Если текущий не в отфильтрованных — сбрасываем на авто
    const inList = isSquare
      ? filteredSepProfiles.some(r => `${r.profile}-${r.wallThickness}` === curKey)
      : filteredSepDiameters.some(r => `${r.outerDiameter}-${r.wallThickness}` === curKey);
    if (!inList) {
      setCharacteristics(p => ({ ...p, separatorDiameter: optKey }));
    }
  }, [optimalSepDiameter, filteredSepDiameters, filteredSepProfiles, isSquare, characteristics.separatorDiameter, clientSizingAutoLocked]);

  // Пресеты клиента: гидроразделитель + гребёнка из общих таблиц (по мощности и по расходу в таблице).
  // Ключ пресета отстаёт до blur (committedThermal). Свыше каталога живой расход уже over-table,
  // а ключ ещё старый: автоподбор меняет тип подключения и этот эффект затирал большие ⌀ табличными.
  useEffect(() => {
    if (viewMode !== 'clients') return;
    if (isClientOverTablePower) return;
    if (characteristics.inputMode !== 'power' && characteristics.inputMode !== 'flowRate') return;
    if (clientPresetPowerKeyForCircuits == null) return;
    const hp = HYDRAULIC_SEPARATOR_CLIENT_PARAMS_TABLE[clientPresetPowerKeyForCircuits];
    const dp = DISTRIBUTION_COLLECTOR_CLIENT_PARAMS_TABLE[clientPresetPowerKeyForCircuits];
    if (!hp || !dp) return;

    setCharacteristics((prev) => {
      const next = { ...prev };
      if (prev.shape === 'round') {
        next.separatorDiameter = `${hp.diameter}-${hp.wallThickness}`;
        if (clientUsesPresetCollectorSize(prev) && dp.diameter != null) {
          next.collectorDiameter = dp.diameter;
        }
      } else {
        const sqWt = hp.squareWallThickness ?? 2;
        next.separatorDiameter = `${hp.squareProfile}-${sqWt}`;
        if (clientUsesPresetCollectorSize(prev) && dp.squareProfile) {
          next.collectorProfile = dp.squareProfile;
        }
      }
      next.pressure = resolvePresetPressure(dp, prev.shape === 'square');

      if (
        clientUsesCatalogPresetConnection(prev, {
          overTable: false,
          constructionKeyKw: clientPresetPowerKeyForCircuits,
        })
      ) {
        const connType = getHydraulicSeparatorPresetConnectionType(clientPresetPowerKeyForCircuits);
        next.connectionType = connType;
        const connSize = getHydraulicSeparatorPresetConnectionSize(
          clientPresetPowerKeyForCircuits,
          connType
        );
        next.connectionSize = connSize ?? null;
      }

      const unchanged =
        prev.separatorDiameter === next.separatorDiameter &&
        prev.collectorDiameter === next.collectorDiameter &&
        prev.collectorProfile === next.collectorProfile &&
        prev.connectionType === next.connectionType &&
        prev.connectionSize === next.connectionSize &&
        Number(prev.pressure) === Number(next.pressure);
      return unchanged ? prev : next;
    });
  }, [
    clientPresetPowerKeyForCircuits,
    isClientOverTablePower,
    viewMode,
    characteristics.inputMode,
    characteristics.shape,
    characteristics.connectionType,
    characteristics.clientPresetConnectionType,
    characteristics.clientPresetCollectorSize,
  ]);

  // Скорость в боковом подключении разделителя
  const sepConnectionVelocity = useMemo(() => {
    const cs = characteristics.connectionSize;
    if (!cs) return null;
    const ct = characteristics.connectionType;
    const wt = getSepConnectionWallThickness(ct);
    const sizeData = SEP_CONNECTION_SIZES.find(s => s.diameter === cs);
    if (!sizeData) return null;
    const effD = getSepTransitionPipeDiameter(ct, sizeData.isFlangeSize ? sizeData.nominalDiameter : cs);
    return calculateVelocity(actualFlowRate, effD, wt);
  }, [actualFlowRate, characteristics.connectionSize, characteristics.connectionType]);

  // ─── Производные: коллектор ───────────────────────────────────────────────
  const availableCollectorDiameters = useMemo(() => getAvailableDiameters(), []);

  /** Верхний лимит мощности для поля ввода */
  const maxPowerKwForInputs = useMemo(() => {
    const dt = effectiveDeltaTemp;
    if (viewMode === 'clients') {
      if (characteristics.shape === 'square') return 600;
      return getMaxThermalPowerKwCollectorCharacteristics('round', dt);
    }
    if (characteristics.shape === 'square') {
      return getMaxThermalPowerKwSeparatorSquareCorpus(dt);
    }
    return getMaxThermalPowerKwCollectorCharacteristics(characteristics.shape, dt);
  }, [viewMode, characteristics.shape, effectiveDeltaTemp]);

  const maxFlowRateForPowerLimit = useMemo(() => {
    const dt = effectiveDeltaTemp;
    if (!dt) return Number.POSITIVE_INFINITY;
    return calculateFlowRateFromPower(maxPowerKwForInputs, dt);
  }, [maxPowerKwForInputs, effectiveDeltaTemp]);

  // Фильтрация диаметров коллектора: скорость в гребёнке + совместимость ⌀ с корпусом стрелки (INSERTION_DEPTH_TABLE / правила узла)
  const filteredCollectorDiameters = useMemo(() => {
    const q = actualFlowRate;
    return availableCollectorDiameters.filter(({ value, wallThickness }) => {
      const inner = value - 2 * (wallThickness ?? 2);
      const area = Math.PI * Math.pow(inner / 1000 / 2, 2);
      const v = area > 0 ? (q / 3600) / area : 0;
      if (v < 0.2 || v > 0.8) return false;
      if (!isSquare && effectiveSepOd != null
        && !isRoundCollectorPipeCompatibleWithSeparator(effectiveSepOd, value)) return false;
      return true;
    });
  }, [availableCollectorDiameters, actualFlowRate, isSquare, effectiveSepOd]);

  /** Квадратная гребёнка: скорость 0,2–0,8 м/с и профиль не шире профиля стрелки */
  const filteredSquareCollectorProfiles = useMemo(() => {
    const q = actualFlowRate;
    return COLLECTOR_SQUARE_PROFILES.filter(({ side, value }) => {
      if (isDistributionClientPresetSquareVelocityException(clientPresetPowerKey, value)) {
        if (isSquare && effectiveSepProfile
          && !isSquareCollectorProfileCompatibleWithSeparator(effectiveSepProfile, value)) {
          return false;
        }
        return true;
      }
      const wt = 2;
      const inner = side - 2 * wt;
      const area = (inner / 1000) * (inner / 1000);
      const v = area > 0 ? (q / 3600) / area : 0;
      if (v < 0.2 || v > 0.8) return false;
      if (isSquare && effectiveSepProfile
        && !isSquareCollectorProfileCompatibleWithSeparator(effectiveSepProfile, value)) return false;
      return true;
    });
  }, [actualFlowRate, isSquare, effectiveSepProfile, clientPresetPowerKey]);

  const clientCollectorPresetOnForCircuits =
    viewMode === 'clients' &&
    !isClientOverTablePowerForCircuits &&
    clientPresetPowerKeyForCircuits != null &&
    clientUsesPresetCollectorSize(charForCircuitSizing);

  const filteredCollectorDiametersForCircuits = useMemo(() => {
    if (clientCollectorPresetOnForCircuits) {
      const dp = DISTRIBUTION_COLLECTOR_CLIENT_PARAMS_TABLE[clientPresetPowerKeyForCircuits];
      const d = dp?.diameter;
      if (d == null) return [];
      const row = availableCollectorDiameters.find((x) => x.value === d);
      return row ? [row] : [{ value: d, wallThickness: 2 }];
    }
    const q = actualFlowRateForCircuits;
    return availableCollectorDiameters.filter(({ value, wallThickness }) => {
      const inner = value - 2 * (wallThickness ?? 2);
      const area = Math.PI * Math.pow(inner / 1000 / 2, 2);
      const v = area > 0 ? (q / 3600) / area : 0;
      if (v < 0.2 || v > 0.8) return false;
      if (!isSquare && effectiveSepOdForCircuits != null
        && !isRoundCollectorPipeCompatibleWithSeparator(effectiveSepOdForCircuits, value)) return false;
      return true;
    });
  }, [
    availableCollectorDiameters,
    actualFlowRateForCircuits,
    isSquare,
    effectiveSepOdForCircuits,
    clientCollectorPresetOnForCircuits,
    clientPresetPowerKeyForCircuits,
  ]);

  const filteredSquareCollectorProfilesForCircuits = useMemo(() => {
    if (clientCollectorPresetOnForCircuits) {
      const dp = DISTRIBUTION_COLLECTOR_CLIENT_PARAMS_TABLE[clientPresetPowerKeyForCircuits];
      const prof = dp?.squareProfile;
      if (!prof) return [];
      const row = COLLECTOR_SQUARE_PROFILES.find((p) => p.value === prof);
      return row ? [row] : [];
    }
    const q = actualFlowRateForCircuits;
    return COLLECTOR_SQUARE_PROFILES.filter(({ side, value }) => {
      if (isDistributionClientPresetSquareVelocityException(clientPresetPowerKeyForCircuits, value)) {
        if (isSquare && effectiveSepProfileForCircuits
          && !isSquareCollectorProfileCompatibleWithSeparator(effectiveSepProfileForCircuits, value)) {
          return false;
        }
        return true;
      }
      const wt = 2;
      const inner = side - 2 * wt;
      const area = (inner / 1000) * (inner / 1000);
      const v = area > 0 ? (q / 3600) / area : 0;
      if (v < 0.2 || v > 0.8) return false;
      if (isSquare && effectiveSepProfileForCircuits
        && !isSquareCollectorProfileCompatibleWithSeparator(effectiveSepProfileForCircuits, value)) return false;
      return true;
    });
  }, [
    actualFlowRateForCircuits,
    isSquare,
    effectiveSepProfileForCircuits,
    clientCollectorPresetOnForCircuits,
    clientPresetPowerKeyForCircuits,
  ]);

  const hasValidHydraulicSeparatorSizing = isSquare
    ? filteredSepProfiles.length > 0
    : filteredSepDiameters.length > 0;

  const hasValidCollectorSizing = isSquare
    ? filteredSquareCollectorProfiles.length > 0
    : filteredCollectorDiameters.length > 0;

  /**
   * Клиент + табличный пресет (до 1000 кВт, любой режим ввода): размеры из таблиц, без подбора по скорости.
   * Свыше таблицы — как инженеры: фильтры по 0,09–0,25 / 0,2–0,8 м/с.
   */
  const clientTablePresetSkipsCollectorVelocityCheck =
    viewMode === 'clients' &&
    clientPresetPowerKey != null &&
    !isClientOverTablePower &&
    clientPresetCollectorSizeOn;

  const hasValidFlowSizing =
    clientTablePresetSkipsCollectorVelocityCheck ||
    (hasValidHydraulicSeparatorSizing && hasValidCollectorSizing);

  const powerFlowSizingHelperText = useMemo(() => {
    if (hasValidFlowSizing) {
      if (characteristics.inputMode === 'power') {
        if (viewMode === 'clients') {
          return `От ${HYDRAULIC_SEPARATOR_CLIENT_POWER_STEPS[0]} до ${maxPowerKwForInputs} кВт`;
        }
        return `От ${THERMAL_POWER_INPUT_MIN_KW} до ${maxPowerKwForInputs} кВт`;
      }
      return `Расход ограничен мощностью до ${maxPowerKwForInputs} кВт при ΔT = ${effectiveDeltaTemp}°C`;
    }
    const parts = [];
    if (!hasValidHydraulicSeparatorSizing) {
      parts.push('нет корпуса гидравлического разделителя в диапазоне скорости 0,09–0,25 м/с');
    }
    if (!hasValidCollectorSizing) {
      parts.push('нет сечения распределительного коллектора в диапазоне скорости 0,2–0,8 м/с в гребёнке');
    }
    const reason = parts.length === 2 ? `${parts[0]} и ${parts[1]}` : parts[0];
    return `При текущем расходе ${reason}. Измените мощность, расход или ΔT.`;
  }, [
    hasValidFlowSizing,
    hasValidHydraulicSeparatorSizing,
    hasValidCollectorSizing,
    characteristics.inputMode,
    effectiveDeltaTemp,
    maxPowerKwForInputs,
    viewMode,
  ]);

  const optimalSquareCollectorProfile = useMemo(() => {
    if (!isSquare || !filteredSquareCollectorProfiles.length) return null;
    const q = actualFlowRate;
    const targetV = isClientOverTablePower
      ? CWS_CLIENT_OVER_TABLE_COLLECTOR_TARGET_VELOCITY_MS
      : 0.4;
    let best = filteredSquareCollectorProfiles[0];
    let bestDiff = Infinity;
    for (const row of filteredSquareCollectorProfiles) {
      const wt = 2;
      const inner = row.side - 2 * wt;
      const area = (inner / 1000) * (inner / 1000);
      const v = area > 0 ? (q / 3600) / area : 0;
      const diff = Math.abs(v - targetV);
      if (diff < bestDiff) {
        bestDiff = diff;
        best = row;
      }
    }
    return best;
  }, [isSquare, filteredSquareCollectorProfiles, actualFlowRate, isClientOverTablePower]);

  const optimalCollectorDiameter = useMemo(() => {
    if (isSquare) return null;
    const q = actualFlowRate;
    const targetV = isClientOverTablePower
      ? CWS_CLIENT_OVER_TABLE_COLLECTOR_TARGET_VELOCITY_MS
      : 0.4;
    let best = filteredCollectorDiameters[0], bestDiff = Infinity;
    for (const d of filteredCollectorDiameters) {
      const inner = d.value - 2 * (d.wallThickness ?? 2);
      const area = Math.PI * Math.pow(inner / 1000 / 2, 2);
      const v = area > 0 ? (q / 3600) / area : 0;
      const diff = Math.abs(v - targetV);
      if (diff < bestDiff) { bestDiff = diff; best = d; }
    }
    return best ?? null;
  }, [isSquare, filteredCollectorDiameters, actualFlowRate, isClientOverTablePower]);

  const selectedCollectorDiameter = useMemo(() => {
    if (isSquare) return null;
    if (characteristics.collectorDiameter != null) return Number(characteristics.collectorDiameter);
    return optimalCollectorDiameter?.value ?? null;
  }, [isSquare, characteristics.collectorDiameter, optimalCollectorDiameter]);

  const selectedCollectorProfile = useMemo(() => {
    if (!isSquare) return null;
    if (characteristics.collectorProfile != null) return characteristics.collectorProfile;
    return optimalSquareCollectorProfile?.value ?? null;
  }, [isSquare, characteristics.collectorProfile, optimalSquareCollectorProfile]);

  const optimalSquareCollectorProfileForCircuits = useMemo(() => {
    if (!isSquare || !filteredSquareCollectorProfilesForCircuits.length) return null;
    const q = actualFlowRateForCircuits;
    const targetV = isClientOverTablePowerForCircuits
      ? CWS_CLIENT_OVER_TABLE_COLLECTOR_TARGET_VELOCITY_MS
      : 0.4;
    let best = filteredSquareCollectorProfilesForCircuits[0];
    let bestDiff = Infinity;
    for (const row of filteredSquareCollectorProfilesForCircuits) {
      const wt = 2;
      const inner = row.side - 2 * wt;
      const area = (inner / 1000) * (inner / 1000);
      const v = area > 0 ? (q / 3600) / area : 0;
      const diff = Math.abs(v - targetV);
      if (diff < bestDiff) {
        bestDiff = diff;
        best = row;
      }
    }
    return best;
  }, [isSquare, filteredSquareCollectorProfilesForCircuits, actualFlowRateForCircuits, isClientOverTablePowerForCircuits]);

  const optimalCollectorDiameterForCircuits = useMemo(() => {
    if (isSquare) return null;
    const q = actualFlowRateForCircuits;
    const targetV = isClientOverTablePowerForCircuits
      ? CWS_CLIENT_OVER_TABLE_COLLECTOR_TARGET_VELOCITY_MS
      : 0.4;
    let best = filteredCollectorDiametersForCircuits[0], bestDiff = Infinity;
    for (const d of filteredCollectorDiametersForCircuits) {
      const inner = d.value - 2 * (d.wallThickness ?? 2);
      const area = Math.PI * Math.pow(inner / 1000 / 2, 2);
      const v = area > 0 ? (q / 3600) / area : 0;
      const diff = Math.abs(v - targetV);
      if (diff < bestDiff) { bestDiff = diff; best = d; }
    }
    return best ?? null;
  }, [isSquare, filteredCollectorDiametersForCircuits, actualFlowRateForCircuits, isClientOverTablePowerForCircuits]);

  const selectedCollectorDiameterForCircuits = useMemo(() => {
    if (isSquare) return null;
    if (characteristics.collectorDiameter != null) return Number(characteristics.collectorDiameter);
    return optimalCollectorDiameterForCircuits?.value ?? null;
  }, [isSquare, characteristics.collectorDiameter, optimalCollectorDiameterForCircuits]);

  const selectedCollectorProfileForCircuits = useMemo(() => {
    if (!isSquare) return null;
    if (characteristics.collectorProfile != null) return characteristics.collectorProfile;
    return optimalSquareCollectorProfileForCircuits?.value ?? null;
  }, [isSquare, characteristics.collectorProfile, optimalSquareCollectorProfileForCircuits]);

  const hasValidCollectorDiameter = useMemo(() => {
    if (clientCatalogCollectorPresetOn) {
      const dp = DISTRIBUTION_COLLECTOR_CLIENT_PARAMS_TABLE[clientPresetPowerKey];
      if (!dp) return false;
      return isSquare ? Boolean(dp.squareProfile) : dp.diameter != null;
    }
    return isSquare ? Boolean(selectedCollectorProfile) : Boolean(selectedCollectorDiameter);
  }, [
    clientCatalogCollectorPresetOn,
    clientPresetPowerKey,
    isSquare,
    selectedCollectorProfile,
    selectedCollectorDiameter,
  ]);

  // Скорость в коллекторе
  const collectorVelocity = useMemo(() => {
    if (isSquare && selectedCollectorProfile) {
      const wt = 2;
      const side = parseInt(selectedCollectorProfile.split('x')[0]);
      const inner = side - 2 * wt;
      const area = (inner / 1000) * (inner / 1000);
      return area > 0 ? (actualFlowRate / 3600) / area : null;
    }
    if (!isSquare && selectedCollectorDiameter) {
      const d = availableCollectorDiameters.find(d => d.value === selectedCollectorDiameter);
      const wt = d?.wallThickness ?? 2;
      const inner = selectedCollectorDiameter - 2 * wt;
      const area = Math.PI * Math.pow(inner / 1000 / 2, 2);
      return area > 0 ? (actualFlowRate / 3600) / area : null;
    }
    return null;
  }, [isSquare, selectedCollectorDiameter, selectedCollectorProfile,
      availableCollectorDiameters, actualFlowRate]);

  // Совместимость фитингов контуров
  const circuitCompatFn = useMemo(() => {
    if (isSquare && selectedCollectorProfileForCircuits)
      return (id) => isDistributionCircuitFittingCompatibleWithSquareProfile(id, selectedCollectorProfileForCircuits);
    if (!isSquare && selectedCollectorDiameterForCircuits)
      return (id) => isDistributionCircuitFittingCompatibleWithRoundPipe(id, selectedCollectorDiameterForCircuits);
    return () => true;
  }, [isSquare, selectedCollectorDiameterForCircuits, selectedCollectorProfileForCircuits]);

  // Поджать контуры и правый торец при смене ⌀/профиля (как в CollectorCharacteristicsCalculator)
  useEffect(() => {
    if (isSquare) {
      if (!selectedCollectorProfileForCircuits) return;
      const circuitCompat = (id) => isDistributionCircuitFittingCompatibleWithSquareProfile(id, selectedCollectorProfileForCircuits);
      const endCompat = (id) => isEndFittingCompatibleWithSquareProfile(id, selectedCollectorProfileForCircuits);
      setSupplyCircuits((prev) => clampCircuitsToCompatibility(prev, circuitCompat));
      setReturnCircuits((prev) => clampCircuitsToCompatibility(prev, circuitCompat));
      setRightEndCircuit((prev) => clampRightEndCircuitToCompatibility(prev, endCompat));
      return;
    }
    if (selectedCollectorDiameterForCircuits == null) return;
    const circuitCompat = (id) => isDistributionCircuitFittingCompatibleWithRoundPipe(id, selectedCollectorDiameterForCircuits);
    const endCompat = (id) => isEndFittingCompatibleWithMainPipe(id, selectedCollectorDiameterForCircuits);
    setSupplyCircuits((prev) => clampCircuitsToCompatibility(prev, circuitCompat));
    setReturnCircuits((prev) => clampCircuitsToCompatibility(prev, circuitCompat));
    setRightEndCircuit((prev) => clampRightEndCircuitToCompatibility(prev, endCompat));
  }, [
    isSquare,
    selectedCollectorDiameterForCircuits,
    selectedCollectorProfileForCircuits,
    supplyCircuits.length,
    returnCircuits.length,
  ]);

  useEffect(() => {
    if (clientCollectorSizingAutoLocked) return;
    if (!isSquare || !characteristics.collectorProfile) return;
    const ok = filteredSquareCollectorProfilesForCircuits.some((p) => p.value === characteristics.collectorProfile);
    if (!ok) setCharacteristics((prev) => ({ ...prev, collectorProfile: null }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredSquareCollectorProfilesForCircuits, isSquare, clientCollectorSizingAutoLocked]);

  useEffect(() => {
    if (clientCollectorSizingAutoLocked) return;
    if (isSquare || characteristics.collectorDiameter == null) return;
    const ok = filteredCollectorDiametersForCircuits.some((d) => d.value === characteristics.collectorDiameter);
    if (!ok) setCharacteristics((prev) => ({ ...prev, collectorDiameter: null }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredCollectorDiametersForCircuits, isSquare, clientCollectorSizingAutoLocked]);

  // Клиент, мощность свыше таблицы: стрелка (~0,21 м/с) и фланец (~1 м/с).
  // Гребёнка (~0,6 м/с) перезаписывается только при включённой галочке стандартных изделий.
  useEffect(() => {
    if (viewMode !== 'clients' || !isClientOverTablePower) return;
    if (actualFlowRate <= 0) return;
    const sepOpt = optimalSepDiameter;
    if (!sepOpt) return;

    const sepKey = isSquare
      ? `${sepOpt.profile}-${sepOpt.wallThickness}`
      : `${sepOpt.outerDiameter}-${sepOpt.wallThickness}`;
    const mainOd = !isSquare ? sepOpt.outerDiameter : parseInt(sepOpt.profile.split('x')[0], 10);
    const mainProf = isSquare ? sepOpt.profile : null;
    const flangeList = filterSepConnectionSizesForType(
      actualFlowRate,
      'flange',
      Number.isFinite(mainOd) ? mainOd : null,
      mainProf,
      isSquare,
    );
    let connSize = null;
    if (flangeList.length > 0) {
      const wtFl = getSepConnectionWallThickness('flange');
      let bestS = flangeList[0];
      let bestDiff = Infinity;
      for (const s of flangeList) {
        const idSize = s.isFlangeSize ? s.nominalDiameter : s.diameter;
        const effD = getSepTransitionPipeDiameter('flange', idSize);
        const v = calculateVelocity(actualFlowRate, effD, wtFl);
        const diff = Math.abs(v - CWS_CLIENT_OVER_TABLE_SOURCE_CONNECTION_TARGET_VELOCITY_MS);
        if (diff < bestDiff) {
          bestDiff = diff;
          bestS = s;
        }
      }
      connSize = bestS.diameter;
    }

    const q = actualFlowRate;
    const targetCol = CWS_CLIENT_OVER_TABLE_COLLECTOR_TARGET_VELOCITY_MS;
    let nextCollectorDiameter = null;
    let nextCollectorProfile = null;
    if (!isSquare) {
      const sepOuter = sepOpt.outerDiameter;
      const candidates = availableCollectorDiameters.filter(({ value, wallThickness }) => {
        const inner = value - 2 * (wallThickness ?? 2);
        const area = Math.PI * Math.pow(inner / 1000 / 2, 2);
        const v = area > 0 ? (q / 3600) / area : 0;
        if (v < 0.2 || v > 0.8) return false;
        if (!isRoundCollectorPipeCompatibleWithSeparator(sepOuter, value)) return false;
        return true;
      });
      let bestD = candidates[0];
      let bestDiffD = Infinity;
      for (const d of candidates) {
        const inner = d.value - 2 * (d.wallThickness ?? 2);
        const area = Math.PI * Math.pow(inner / 1000 / 2, 2);
        const v = area > 0 ? (q / 3600) / area : 0;
        const diff = Math.abs(v - targetCol);
        if (diff < bestDiffD) {
          bestDiffD = diff;
          bestD = d;
        }
      }
      nextCollectorDiameter = bestD?.value ?? null;
    } else {
      const sepProf = sepOpt.profile;
      const candidates = COLLECTOR_SQUARE_PROFILES.filter(({ side, value }) => {
        const wt = 2;
        const inner = side - 2 * wt;
        const area = (inner / 1000) * (inner / 1000);
        const v = area > 0 ? (q / 3600) / area : 0;
        if (v < 0.2 || v > 0.8) return false;
        if (sepProf && !isSquareCollectorProfileCompatibleWithSeparator(sepProf, value)) return false;
        return true;
      });
      let bestP = candidates[0];
      let bestDiffP = Infinity;
      for (const row of candidates) {
        const wt = 2;
        const inner = row.side - 2 * wt;
        const area = (inner / 1000) * (inner / 1000);
        const v = area > 0 ? (q / 3600) / area : 0;
        const diff = Math.abs(v - targetCol);
        if (diff < bestDiffP) {
          bestDiffP = diff;
          bestP = row;
        }
      }
      nextCollectorProfile = bestP?.value ?? null;
    }

    setCharacteristics((prev) => {
      const presetConnectionOverTable = clientUsesPresetConnectionType(prev);
      const connectionType = presetConnectionOverTable ? 'flange' : prev.connectionType;
      let sizedConn = connSize;
      if (!presetConnectionOverTable && connectionType !== 'flange') {
        const list = filterSepConnectionSizesForType(
          actualFlowRate,
          connectionType,
          Number.isFinite(mainOd) ? mainOd : null,
          mainProf,
          isSquare,
        );
        if (list.length > 0) {
          const wt = getSepConnectionWallThickness(connectionType);
          let bestS = list[0];
          let bestDiff = Infinity;
          for (const s of list) {
            const idSize = s.isFlangeSize ? s.nominalDiameter : s.diameter;
            const effD = getSepTransitionPipeDiameter(connectionType, idSize);
            const v = calculateVelocity(actualFlowRate, effD, wt);
            const diff = Math.abs(v - CWS_CLIENT_OVER_TABLE_SOURCE_CONNECTION_TARGET_VELOCITY_MS);
            if (diff < bestDiff) {
              bestDiff = diff;
              bestS = s;
            }
          }
          sizedConn = bestS.diameter;
        } else {
          sizedConn = prev.connectionSize;
        }
      }

      const lockCollector = clientUsesPresetCollectorSize(prev);
      const next = {
        ...prev,
        separatorDiameter: sepKey,
        connectionType,
        connectionSize: sizedConn,
        ...(lockCollector
          ? {
              collectorDiameter: isSquare ? null : nextCollectorDiameter,
              collectorProfile: isSquare ? nextCollectorProfile : null,
            }
          : {}),
      };
      const unchanged =
        prev.separatorDiameter === next.separatorDiameter &&
        prev.connectionType === next.connectionType &&
        prev.connectionSize === next.connectionSize &&
        (!lockCollector ||
          (isSquare
            ? prev.collectorProfile === next.collectorProfile
            : prev.collectorDiameter === next.collectorDiameter));
      return unchanged ? prev : next;
    });
  }, [
    viewMode,
    isClientOverTablePower,
    actualFlowRate,
    isSquare,
    optimalSepDiameter,
    availableCollectorDiameters,
    characteristics.shape,
    characteristics.clientPresetCollectorSize,
  ]);

  // ─── updateCharacteristic ─────────────────────────────────────────────────
  const clampPowerAndFlowToMax = (next) => {
    let maxP;
    if (viewMode === 'clients') {
      maxP =
        next.shape === 'square'
          ? 600
          : getMaxThermalPowerKwCollectorCharacteristics(
              'round',
              normalizeClientThermalDeltaTemp(next.deltaTemp)
            );
    } else {
      maxP =
        next.shape === 'square'
          ? getMaxThermalPowerKwSeparatorSquareCorpus(next.deltaTemp)
          : getMaxThermalPowerKwCollectorCharacteristics(next.shape, next.deltaTemp);
    }
    const dt = normalizeClientThermalDeltaTemp(next.deltaTemp);
    if (
      next.inputMode === 'power' &&
      next.power !== '' &&
      typeof next.power === 'number' &&
      !Number.isNaN(next.power) &&
      next.power > maxP
    ) {
      next.power = maxP;
    }
    if (
      next.inputMode === 'flowRate' &&
      next.flowRate !== '' &&
      next.flowRate != null &&
      Number.isFinite(dt) &&
      dt > 0
    ) {
      const fp = calculatePowerFromFlowRate(Number(next.flowRate), dt);
      if (fp > maxP) {
        next.flowRate = parseFloat(calculateFlowRateFromPower(maxP, dt).toFixed(2));
      }
    }
  };

  const updateCharacteristic = (key, value, options) => {
    setCharacteristics(prev => {
      const next = { ...prev, [key]: value };
      if (key === 'deltaTemp') {
        const dt = Number(value);
        if (ALLOWED_DELTA_TEMPS.includes(dt) && viewMode === 'engineers') {
          preservedCwsEngineersDeltaRef.current = dt;
        }
      }

      if (viewMode === 'clients' && key === 'power' && options?.finalizeClientPreset) {
        const p = typeof next.power === 'number' ? next.power : parseFloat(next.power);
        if (Number.isFinite(p)) {
          applyCwsClientPowerPresetSnap(next, p);
        }
      }

      if (viewMode === 'clients' && key === 'clientPresetDeltaTemp') {
        next.clientPresetDeltaTemp = Boolean(value);
        if (next.inputMode === 'power') {
          syncCwsClientThermalFlowFromPower(next);
        }
        applyCwsClientDeltaTempFromConstructionPreset(next);
      }

      if (viewMode === 'clients' && key === 'clientPresetConnectionType') {
        next.clientPresetConnectionType = Boolean(value);
        if (next.clientPresetConnectionType) {
          applyCwsClientConnectionFromConstructionPreset(next);
        } else {
          seedCwsClientConnectionFromCatalog(next);
        }
      }

      if (viewMode === 'clients' && key === 'clientPresetCollectorSize') {
        next.clientPresetCollectorSize = Boolean(value);
        if (next.clientPresetCollectorSize) {
          applyCwsClientCollectorFromConstructionPreset(next);
        } else {
          seedCwsClientCollectorFromCatalog(next);
        }
      }

      if (viewMode === 'clients' && key === 'deltaTemp') {
        if (clientUsesPresetDeltaTemp(next)) {
          return prev;
        }
        next.deltaTemp = normalizeClientThermalDeltaTemp(Number(value));
        if (next.inputMode === 'power') {
          syncCwsClientThermalFlowFromPower(next);
        }
      }
      if (viewMode === 'clients' && key === 'flowRate') {
        const qn =
          typeof next.flowRate === 'number' ? next.flowRate : parseFloat(next.flowRate);
        if (Number.isFinite(qn) && qn > 0) {
          const presetKey = clientPresetKeyFromFlowM3h(
            qn,
            normalizeClientThermalDeltaTemp(next.deltaTemp),
            next.shape
          );
          if (presetKey != null && clientUsesPresetDeltaTemp(next)) {
            next.deltaTemp = getCwsPresetReferenceDeltaTemp(presetKey);
          }
        }
      }
      if (key === 'inputMode' && value === 'flowRate') {
        const p = prev.power === '' ? THERMAL_POWER_INPUT_MIN_KW : Number(prev.power);
        const effP = !Number.isFinite(p) || p <= 0 ? THERMAL_POWER_INPUT_MIN_KW : p;
        const dt = normalizeClientThermalDeltaTemp(prev.deltaTemp);
        next.flowRate = parseFloat(calculateFlowRateFromPower(effP, dt).toFixed(2));
      }
      if (key === 'inputMode' && value === 'power') {
        const q = prev.flowRate === '' ? 0 : Number(prev.flowRate);
        const dt = normalizeClientThermalDeltaTemp(prev.deltaTemp);
        if (viewMode === 'clients' && Number.isFinite(q) && q > 0) {
          const presetKey = clientPresetKeyFromFlowM3h(q, dt, prev.shape);
          if (presetKey != null) {
            next.power = clampCwsClientPowerKw(
              parseFloat(calculatePowerFromFlowRate(q, dt).toFixed(2)),
              CWS_CLIENT_POWER_MAX_KW
            );
            syncCwsClientThermalFlowFromPower(next);
            applyCwsClientDeltaTempFromConstructionPreset(next);
            applyCwsClientConnectionFromConstructionPreset(next);
          } else {
            next.power = parseFloat(calculatePowerFromFlowRate(q, dt).toFixed(2));
          }
        } else {
          next.power = parseFloat(calculatePowerFromFlowRate(q, dt).toFixed(2));
        }
      }
      if (key === 'shape') {
        next.collectorDiameter = null;
        next.collectorProfile = null;
        next.separatorDiameter = null;
        next.connectionSize = null;
        next.pressure = value === 'square' ? 4 : 6;
        clampPowerAndFlowToMax(next);
      }
      if (key === 'deltaTemp') {
        clampPowerAndFlowToMax(next);
      }
      if (key === 'separatorDiameter' && next.shape === 'round' && value) {
        const { od } = parseSeparatorDiameterKey(value);
        if (roundSeparatorDisallowsSixEightBar(od) && Number(next.pressure) === 6) {
          next.pressure = 8;
        }
      }
      if (key === 'collectorDiameter' && next.shape === 'round' && value != null) {
        const od = Number(value);
        if (!Number.isNaN(od) && roundCollectorDisallowsSixEightBar(od) && Number(next.pressure) === 6) {
          next.pressure = 8;
        }
      }
      if (key === 'pressure' && next.shape === 'round' && Number(value) === 6) {
        const { od: sepOd } = parseSeparatorDiameterKey(next.separatorDiameter);
        const colOd = next.collectorDiameter != null ? Number(next.collectorDiameter) : null;
        if (roundSeparatorDisallowsSixEightBar(sepOd)) next.pressure = 8;
        else if (colOd != null && !Number.isNaN(colOd) && roundCollectorDisallowsSixEightBar(colOd)) {
          next.pressure = 8;
        }
      }
      return next;
    });
  };

  // ─── Вычисление отображаемых значений ─────────────────────────────────────
  const displayFlowRate =
    viewMode === 'clients' && clientThermalFlowM3h != null
      ? clientThermalFlowM3h.toFixed(2)
      : actualFlowRate.toFixed(2);
  const displayPower = characteristics.inputMode === 'flowRate'
    ? (characteristics.flowRate === ''
      ? ''
      : calculatePowerFromFlowRate(Number(characteristics.flowRate), effectiveDeltaTemp).toFixed(2))
    : null;

  const roundCollectorNoStraightCap =
    !isSquare && selectedCollectorDiameter != null &&
    roundCollectorDisallowsSixEightBar(selectedCollectorDiameter);

  /** 6/8 бар для круга недоступно, если такую комбинацию нельзя набрать ни на стрелке, ни на гребёнке. */
  const roundPipeNoSixEightPressure = useMemo(() => {
    if (isSquare) return false;
    const sepBad = effectiveSepOd != null && roundSeparatorDisallowsSixEightBar(effectiveSepOd);
    const colBad = selectedCollectorDiameter != null && roundCollectorDisallowsSixEightBar(selectedCollectorDiameter);
    return sepBad || colBad;
  }, [isSquare, effectiveSepOd, selectedCollectorDiameter]);

  useEffect(() => {
    if (isSquare || !roundPipeNoSixEightPressure) return;
    setCharacteristics((prev) => {
      if (Number(prev.pressure) === 8) return prev;
      return { ...prev, pressure: 8 };
    });
  }, [isSquare, roundPipeNoSixEightPressure]);

  useEffect(() => {
    if (!isSquare) return;
    setCharacteristics((prev) => {
      if (Number(prev.pressure) === 4) return prev;
      return { ...prev, pressure: 4 };
    });
  }, [isSquare, characteristics.pressure]);

  // ─── Расчёт параметров ────────────────────────────────────────────────────
  const canCalculate = useMemo(() => {
    if (!hasValidFlowSizing) return false;
    if (!effectiveSepOd && !effectiveSepProfile) return false;
    if (!characteristics.connectionSize) return false;
    if (!hasValidCollectorDiameter) return false;
    return true;
  }, [hasValidFlowSizing, effectiveSepOd, effectiveSepProfile, characteristics.connectionSize, hasValidCollectorDiameter]);

  const calculateParameters = () => {
    if (!canCalculate) return;

    // ── 1. Параметры гидроразделителя ──
    const isSquareShape = isSquare;
    const sepWt = effectiveSepWt;
    // Круг: внутренний ⌀. Квадрат: внутренняя сторона профиля (effectiveSepOd для ключа вида 80x80-2 может быть пустым).
    let sepInnerD;
    if (isSquareShape && effectiveSepProfile) {
      const side = parseInt(String(effectiveSepProfile).split('x')[0], 10);
      sepInnerD = Number.isFinite(side) ? side - 2 * sepWt : NaN;
    } else if (effectiveSepOd != null && Number.isFinite(effectiveSepOd)) {
      sepInnerD = effectiveSepOd - 2 * sepWt;
    } else {
      sepInnerD = NaN;
    }
    const ct = characteristics.connectionType;
    const cs = characteristics.connectionSize;
    const connPerSide = Math.floor((characteristics.connectionsCount ?? 4) / 2);

    const connWt = getSepConnectionWallThickness(ct);
    const connInnerD = cs - 2 * connWt;
    const geomSpacing =
      separatorLateralStepMmFromConnAndSepInner(connInnerD, sepInnerD)
      ?? roundUp5mm(Math.max(3 * connInnerD, 2 * sepInnerD));
    const geomEdgeOffset = roundUp5mm(sepInnerD);

    const hpPreset =
      clientPresetPowerKey != null
        ? HYDRAULIC_SEPARATOR_CLIENT_PARAMS_TABLE[clientPresetPowerKey]
        : null;
    const useClientSepTableOffsets =
      viewMode === 'clients' && clientPresetPowerKey != null && hpPreset != null;

    const heightFromSepSide = (cfg) =>
      cfg.firstOffset + Math.max(0, connPerSide - 1) * cfg.spacing + cfg.lastOffset;

    let separatorHeight;
    let leftConnections;
    let leftConnectionsFirstOffset;
    let leftConnectionsLastOffset;
    let leftConnectionsSpacing;
    /** Межосевое патрубков на стрелке — для pipeSpacing (минус 50 мм) */
    let sepLateralStepForPipeSpacing;

    if (useClientSepTableOffsets) {
      const hpVar = hpPreset[characteristics.connectionType] || null;
      let leftCfg = hpVar?.left ?? hpPreset.left;
      let rightCfg = hpVar?.right ?? hpPreset.right;
      if (
        typeof clientPresetPowerKey === 'number' &&
        clientPresetPowerKey <= CWS_HYDRAULIC_SEP_CLIENT_OFFSET_MAX_PRESET_KW
      ) {
        const ovr = CWS_HYDRAULIC_SEP_CLIENT_OFFSETS_BY_PRESET_KW[clientPresetPowerKey];
        if (ovr?.left && ovr?.right) {
          leftCfg = ovr.left;
          rightCfg = ovr.right;
        }
      }
      if (leftCfg && rightCfg) {
        separatorHeight = Math.round(
          Math.max(heightFromSepSide(leftCfg), heightFromSepSide(rightCfg))
        );
        leftConnectionsFirstOffset = leftCfg.firstOffset;
        leftConnectionsLastOffset = leftCfg.lastOffset;
        leftConnectionsSpacing = leftCfg.spacing;
        sepLateralStepForPipeSpacing = leftCfg.spacing;
        leftConnections = Array.from({ length: connPerSide }, (_, i) => ({
          id: `left-${i + 1}`,
          fittingId: getSepFittingIdFromConnection(ct, cs),
          fittingLength: null,
          offsetFromTop: Math.round(leftCfg.firstOffset + i * leftCfg.spacing),
          offsetDirection: 'top',
          spacingToNext: null,
        }));
      }
    }

    if (!leftConnections?.length) {
      const calcH =
        connPerSide === 1
          ? 2 * geomEdgeOffset
          : 2 * geomEdgeOffset + (connPerSide - 1) * geomSpacing;
      const minH = Math.max(calcH, roundUp5mm(sepInnerD * 3.5));
      separatorHeight = Math.round(minH);
      leftConnectionsFirstOffset = geomEdgeOffset;
      leftConnectionsLastOffset = geomEdgeOffset;
      leftConnectionsSpacing = geomSpacing;
      sepLateralStepForPipeSpacing = geomSpacing;
      leftConnections = [];
      for (let i = 0; i < connPerSide; i++) {
        leftConnections.push({
          id: `left-${i + 1}`,
          fittingId: getSepFittingIdFromConnection(ct, cs),
          fittingLength: null,
          offsetFromTop: Math.round(geomEdgeOffset + i * geomSpacing),
          offsetDirection: 'top',
          spacingToNext: null,
        });
      }
    }
    // rightConnections — в CollectorWithSeparatorEditor нет, гребёнки впритык к правой грани;
    // высота корпуса — как в CharacteristicsCalculator: max(левая сторона, правая) из таблицы клиента

    const sepStraightCatalog =
      !isSquareShape && effectiveSepOd != null &&
      effectiveSepOd <= SEPARATOR_STRAIGHT_CAP_MAX_OUTER_MM &&
      Boolean(findStraightCap(effectiveSepOd));
    const roundCapType = sepStraightCatalog
      ? (Number(characteristics.pressure) < 8 ? 'straight' : 'elliptical')
      : (!isSquareShape ? 'elliptical' : 'straight');

    // ── 2. Параметры распределительного коллектора ──
    const flangeStubLen = (fId) =>
      fId && String(fId).startsWith('Flanec_') ? 100 : null;

    /** Для клиентов с табличным пресетом — только ⌀/профиль из distributionCollectorClientParams, без подбора по скорости */
    const dpPresetRow =
      viewMode === 'clients' &&
      characteristics.inputMode === 'power' &&
      clientPresetPowerKeyForCircuits != null
        ? DISTRIBUTION_COLLECTOR_CLIENT_PARAMS_TABLE[clientPresetPowerKeyForCircuits]
        : null;
    const collectorRoundOuterMm = !isSquareShape
      ? (dpPresetRow?.diameter != null
          ? Number(dpPresetRow.diameter)
          : selectedCollectorDiameterForCircuits)
      : null;
    const collectorSquareProfileMm = isSquareShape
      ? (dpPresetRow?.squareProfile ?? selectedCollectorProfileForCircuits)
      : null;

    // Торцевое подключение только справа (слева — стык с гидрострелкой)
    const hasLeft  = false;
    const hasRight = Boolean(rightEndCircuit);

    let capType;
    let shapeFields;
    if (!isSquareShape) {
      const straightOk = !roundCollectorDisallowsSixEightBar(collectorRoundOuterMm);
      capType = straightOk && Number(characteristics.pressure) < 8 ? 'straight' : 'elliptical';
      shapeFields = {
        shape: 'round',
        supplyPipeDiameter: collectorRoundOuterMm,
        returnPipeDiameter: collectorRoundOuterMm,
        supplyPipeProfile:  '80x80',
        returnPipeProfile:  '80x80',
      };
    } else {
      capType = 'straight';
      shapeFields = {
        shape: 'square',
        supplyPipeDiameter: 76.1,
        returnPipeDiameter: 76.1,
        supplyPipeProfile:  collectorSquareProfileMm,
        returnPipeProfile:  collectorSquareProfileMm,
      };
    }

    // Отступы первого/последнего контура — правила для узла с гидрострелкой (см. getCollectorWithSeparatorFirstLastOffsetsMm)
    const profileSideMm = isSquareShape
      ? parseInt(String(collectorSquareProfileMm).split('x')[0], 10)
      : null;
    const { firstOffset, lastOffset } = getCollectorWithSeparatorFirstLastOffsetsMm({
      supplyCircuits,
      returnCircuits,
      mainPipeOuterMm: isSquareShape ? null : collectorRoundOuterMm,
      isSquare: isSquareShape,
      profileSideMm: Number.isFinite(profileSideMm) ? profileSideMm : null,
    });

    const supplySpacingLayout = computeDistributionCircuitSpacingLayout(supplyCircuits);
    const returnSpacingLayout = computeDistributionCircuitSpacingLayout(returnCircuits);
    const supplyDefaultBetween = supplySpacingLayout.spacingToNext.find(x => x != null) ?? supplySpacingLayout.bases[0] ?? 125;
    const returnDefaultBetween = returnSpacingLayout.spacingToNext.find(x => x != null) ?? returnSpacingLayout.bases[0] ?? 125;

    // Межосевое гребёнок — пресет комбо (40/50 кВт → 90 мм) или collectorInteraxisFromSeparatorConnectionSpacingMm
    const pipeDiameter = isSquareShape ? 76.1 : collectorRoundOuterMm;
    const pipeSpacingOverride =
      viewMode === 'clients' ? getCwsCollectorPipeSpacingMm(clientPresetPowerKey) : null;
    const pipeSpacing =
      pipeSpacingOverride ??
      collectorInteraxisFromSeparatorConnectionSpacingMm(sepLateralStepForPipeSpacing);

    const endCircuitForSide = (side) => (side === 'right' ? rightEndCircuit : null);

    const makePipeConn = (prefix, enabled) => {
      const p = prefix.charAt(0).toLowerCase() + prefix.slice(1);
      const side = prefix.endsWith('Left') ? 'left' : 'right';
      const endCircuit = endCircuitForSide(side);
      const isActive = enabled || Boolean(endCircuit);
      if (!isActive) {
        return {
          [`has${prefix}Connection`]: false,
          [`${p}ConnectionType`]: 'fitting',
          [`${p}ConnectionPipeDiameter`]: pipeDiameter,
          [`${p}ConnectionPipeLength`]: null,
          [`${p}ConnectionFittingCategory`]: '',
          [`${p}ConnectionFitting`]: '',
          [`${p}ConnectionFittingLength`]: null,
          [`${p}ConnectionLength`]: null,
        };
      }
      const connFit = endCircuit?.fitting ?? '';
      const connCat = endCircuit?.fittingCategory ?? '';
      let connPipeDia = pipeDiameter;
      if (connFit) {
        if (connFit.startsWith('Flanec_') || connFit.startsWith('Nak_Gaika_') || connFit.startsWith('Shaib_Nar_Rezba_')) {
          connPipeDia = getPipeDiameterForFitting(connFit) ?? pipeDiameter;
        } else {
          connPipeDia = fittingsData[connFit]?.diameter ?? pipeDiameter;
        }
      }
      return {
        [`has${prefix}Connection`]: true,
        [`${p}ConnectionType`]: 'fitting',
        [`${p}ConnectionPipeDiameter`]: connPipeDia,
        [`${p}ConnectionPipeLength`]: flangeStubLen(connFit),
        [`${p}ConnectionFittingCategory`]: connCat,
        [`${p}ConnectionFitting`]: connFit,
        [`${p}ConnectionFittingLength`]: null,
        [`${p}ConnectionLength`]: null,
      };
    };

    const makeCap = (prefix, active) => {
      const p = prefix.charAt(0).toLowerCase() + prefix.slice(1);
      const side = prefix.endsWith('Left') ? 'left' : 'right';
      const endCircuit = endCircuitForSide(side);
      const connFitId = endCircuit?.fitting ?? '';
      const isActive = active || Boolean(endCircuit);
      if (!isActive || !connFitId) {
        return {
          [`has${prefix}Cap`]: !isActive,
          [`${p}CapElementType`]: 'cap',
          [`${p}CapType`]: capType,
          [`${p}Reducer`]: null,
        };
      }
      const collector = prefix.startsWith('Supply') ? 'supply' : 'return';
      const shape = isSquareShape ? 'square' : 'round';
      const capUpdates = getCapUpdatesForConnectionFitting(connFitId, pipeDiameter, side, collector, shape);
      if (!Object.keys(capUpdates).length) {
        return {
          [`has${prefix}Cap`]: false,
          [`${p}CapElementType`]: 'cap',
          [`${p}CapType`]: capType,
          [`${p}Reducer`]: null,
        };
      }
      const merged = { [`${p}CapType`]: capType, [`${p}Reducer`]: null, ...capUpdates };
      // Разрешаем прямую заглушку при малом давлении
      const elemKey = `${p}CapElementType`;
      const hasKey  = `has${prefix}Cap`;
      const capKey  = `${p}CapType`;
      const userStraight = !isSquareShape &&
        !roundCollectorDisallowsSixEightBar(pipeDiameter) &&
        Number(characteristics.pressure) < 8;
      if (merged[elemKey] !== 'reducer' && userStraight && merged[hasKey] === true && merged[capKey] === 'elliptical') {
        merged[capKey] = 'straight';
      }
      return merged;
    };

    // Центральный фитинг коллектора
    const circuitCount = Math.max(supplyCircuits.length, returnCircuits.length);
    const centerRefRow = supplyCircuits.length >= returnCircuits.length ? 'supply' : 'return';
    const centerRefCount = centerRefRow === 'supply' ? supplyCircuits.length : returnCircuits.length;
    const betweenOptions = Math.max(0, centerRefCount * 2 - 1);
    const centerFittingBetweenIndex =
      betweenOptions >= 1
        ? (lastOffset > 50 ? 0 : Math.floor((betweenOptions - 1) / 2))
        : 0;
    const defaultCenterFitting = {
      id: 1,
      fittingCategory: 'Муфты',
      fittingId: 'Mufta_1.2',
      side: 'both',
      distanceFromStart: 50,
      distanceDirection: 'left',
      betweenCircuits: '1-2',
      referenceRow: centerRefRow,
      positionType: betweenOptions >= 1 ? 'betweenFittings' : 'center',
      betweenIndex: centerFittingBetweenIndex,
    };

    // Доп. выходы гребёнок (как в CollectorCharacteristicsCalculator): круг — по ⌀; квадрат — по стороне профиля (60×60 ≈ ⌀60.3); при lastOffset > 50
    const collectorProfileSideMmForOutlets = isSquareShape && collectorSquareProfileMm
      ? parseInt(String(collectorSquareProfileMm).split('x')[0], 10)
      : NaN;
    const collectorOutletDistanceFromEdge = (() => {
      if (!isSquareShape && collectorRoundOuterMm != null) {
        return collectorRoundOuterMm > 60.3 ? 35 : 25;
      }
      if (isSquareShape && Number.isFinite(collectorProfileSideMmForOutlets)) {
        return collectorProfileSideMmForOutlets > 60 ? 35 : 25;
      }
      return 25;
    })();
    const addExtraOutlets =
      lastOffset > 50 &&
      ((!isSquareShape && collectorRoundOuterMm != null) ||
        (isSquareShape && Number.isFinite(collectorProfileSideMmForOutlets)));
    const defSupplyOutlet = {
      id: 1,
      type: 'fitting',
      diameter: 26.9,
      length: 80,
      fittingCategory: 'Муфты',
      fittingId: 'Mufta_1.2',
      fittingLength: null,
      flangePipeLength: null,
      direction: 'up',
      positionType: 'distance',
      distanceFromEdge: collectorOutletDistanceFromEdge,
      distanceDirection: 'right',
      betweenIndex: 0,
      referenceRow: 'supply',
      outletType: 'short',
    };
    const defReturnOutlet = {
      ...defSupplyOutlet,
      id: 1,
      direction: 'down',
      referenceRow: 'return',
    };

    const enableSeparatorAdditionalOutlets =
      lastOffset > 50 &&
      (isSquareShape ? Boolean(collectorSquareProfileMm) : collectorRoundOuterMm != null);

    const sepOutletDistanceFromEdge = (() => {
      if (!isSquareShape && effectiveSepOd != null && Number.isFinite(effectiveSepOd)) {
        return effectiveSepOd > 60.3 ? 35 : 25;
      }
      if (isSquareShape && effectiveSepProfile) {
        const s = parseInt(String(effectiveSepProfile).split('x')[0], 10);
        if (Number.isFinite(s)) return s > 60 ? 35 : 25;
      }
      return 25;
    })();

    const separatorAdditionalOutlets = enableSeparatorAdditionalOutlets
      ? [
          {
            id: 'sep-add-outlet-1',
            fittingCategory: 'Муфты',
            fittingId: 'Mufta_1.2',
            positionType: 'distance',
            side: 'left',
            fittingLength: null,
            distanceFromEdge: sepOutletDistanceFromEdge,
            distanceDirection: 'left',
          },
          {
            id: 'sep-add-outlet-2',
            fittingCategory: 'Муфты',
            fittingId: 'Mufta_1.2',
            positionType: 'distance',
            side: 'left',
            fittingLength: null,
            distanceFromEdge: sepOutletDistanceFromEdge,
            distanceDirection: 'right',
          },
        ]
      : [];

    // ── 3. Итоговый объект params для CollectorWithSeparatorEditor ──
    const params = {
      // Общее
      shape: isSquareShape ? 'square' : 'round',

      // Гидроразделитель
      ...(isSquareShape
        ? { mainPipeProfile: effectiveSepProfile, mainPipeWallThickness: `x${sepWt}` }
        : { mainPipeDiameter: effectiveSepOd, mainPipeWallThickness: `x${sepWt}` }
      ),
      separatorHeight,

      hasTopCap: true,
      hasBottomCap: true,
      topCapElementType: 'cap',
      bottomCapElementType: 'cap',
      topCapType:    isSquareShape ? 'straight' : roundCapType,
      bottomCapType: isSquareShape ? 'straight' : roundCapType,
      topReducer:    null,
      bottomReducer: null,

      hasTopConnection: true,
      topCapFittingCategory: 'Муфты',
      topCapFitting: 'Mufta_1.2',
      topCapFittingLength: null,
      hasBottomConnection: true,
      bottomCapFittingCategory: 'Муфты',
      bottomCapFitting: 'Mufta_1.2',
      bottomCapFittingLength: null,

      leftConnectionsPlacementMode: 'spacing',
      leftConnectionsFirstOffset:   leftConnectionsFirstOffset,
      leftConnectionsFirstOffsetMode: 'fromEdge',
      leftConnectionsLastOffset:    leftConnectionsLastOffset,
      leftConnectionsSpacing:       leftConnectionsSpacing,
      leftConnections,
      additionalOutlets: separatorAdditionalOutlets,

      // Коллектор
      ...shapeFields,
      pipeSpacing,
      pipeSpacingMode: 'interaxis',

      circuitCount,
      supplyCircuits: supplyCircuits.map((c, i) => ({
        id: `supply-${i}`,
        fitting: c.fitting,
        fittingCategory: c.fittingCategory,
        fittingLength: null,
        flangePipeLength: flangeStubLen(c.fitting),
        spacingToNext: supplySpacingLayout.spacingToNext[i],
        circuitSpacing: supplySpacingLayout.bases[i] ?? 125,
        swapOrder: false,
      })),
      returnCircuits: returnCircuits.map((c, i) => ({
        id: `return-${i}`,
        fitting: c.fitting,
        fittingCategory: c.fittingCategory,
        fittingLength: null,
        flangePipeLength: flangeStubLen(c.fitting),
        spacingToNext: returnSpacingLayout.spacingToNext[i],
        circuitSpacing: returnSpacingLayout.bases[i] ?? 125,
        swapOrder: false,
      })),

      supplyFirstCircuitOffset:             firstOffset,
      supplyFirstCircuitOffsetMode:         'fromEdge',
      supplyFirstCircuitOffsetReferenceIndex: 0,
      supplyFirstCircuitOffsetSaved:        null,
      supplyFirstCircuitOffsetLastUsed:     0,
      supplyLastCircuitOffset:              lastOffset,
      returnFirstCircuitOffset:             firstOffset,
      returnFirstCircuitOffsetMode:         'fromEdge',
      returnFirstCircuitOffsetReferenceIndex: 0,
      returnFirstCircuitOffsetSaved:        null,
      returnFirstCircuitOffsetLastUsed:     0,
      returnLastCircuitOffset:              lastOffset,
      supplyCircuitSpacing:         supplySpacingLayout.bases[0] ?? 125,
      returnCircuitSpacing:         returnSpacingLayout.bases[0] ?? 125,
      supplySpacingBetweenCircuits: supplyDefaultBetween,
      returnSpacingBetweenCircuits: returnDefaultBetween,

      ...makePipeConn('SupplyLeft',  hasLeft),
      ...makePipeConn('SupplyRight', hasRight),
      ...makePipeConn('ReturnLeft',  hasLeft),
      ...makePipeConn('ReturnRight', hasRight),

      ...makeCap('SupplyLeft',  hasLeft),
      ...makeCap('SupplyRight', hasRight),
      ...makeCap('ReturnLeft',  hasLeft),
      ...makeCap('ReturnRight', hasRight),

      supplyCenterFittingCount:  1,
      returnCenterFittingCount:  1,
      supplyCenterFittings:  [{ ...defaultCenterFitting, id: 1 }],
      returnCenterFittings:  [{ ...defaultCenterFitting, id: 1 }],

      supplyAdditionalOutletCount:  addExtraOutlets ? 1 : 0,
      returnAdditionalOutletCount:  addExtraOutlets ? 1 : 0,
      supplyAdditionalOutlets:      addExtraOutlets ? [defSupplyOutlet] : [],
      returnAdditionalOutlets:      addExtraOutlets ? [defReturnOutlet] : [],
      supplyAdditionalFirstOutletOffset: 60,
      returnAdditionalFirstOutletOffset: 60,
      supplyAdditionalLastOutletOffset:  60,
      returnAdditionalLastOutletOffset:  60,
      supplyAdditionalOutletSpacing:     100,
      returnAdditionalOutletSpacing:     100,

      useManualLength:     false,
      manualSupplyLength:  null,
      manualReturnLength:  null,

      isSystemFlipped: false,
      hasPreBoilerCollector: false,

      pressure: characteristics.pressure,

      _calculatedFrom: 'characteristics',
      _calculatorViewMode: viewMode,
      _characteristics: {
        ...characteristics,
        supplyCircuits:  supplyCircuits.map(({ fittingCategory, fitting }) => ({ fittingCategory, fitting })),
        returnCircuits:  returnCircuits.map(({ fittingCategory, fitting }) => ({ fittingCategory, fitting })),
        leftEndCircuit:  null,
        rightEndCircuit: rightEndCircuit ? { fittingCategory: rightEndCircuit.fittingCategory, fitting: rightEndCircuit.fitting } : null,
      },
    };

    onCalculate(params);
  };

  // ─── Рендер ───────────────────────────────────────────────────────────────
  // Определяем что попапу показывать
  const pop = circuitPopover;
  const popCur = pop
    ? (pop.row === 'supply'   ? supplyCircuits[pop.index]
     : pop.row === 'return'   ? returnCircuits[pop.index]
     : pop.row === 'rightEnd' ? rightEndCircuit
     : null)
    : null;

  const compatibilityFn = pop
    ? (pop.row === 'supply' || pop.row === 'return' ? circuitCompatFn
     : pop.row === 'rightEnd'
       ? (isSquare && selectedCollectorProfileForCircuits
           ? (id) => isEndFittingCompatibleWithSquareProfile(id, selectedCollectorProfileForCircuits)
           : !isSquare && selectedCollectorDiameterForCircuits
             ? (id) => isEndFittingCompatibleWithMainPipe(id, selectedCollectorDiameterForCircuits)
             : null)
     : null)
    : null;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e8ec 100%)',
        py: 3,
      }}
    >
      <Container maxWidth="md">
        {/* Шапка — как в CollectorCharacteristicsCalculator / CharacteristicsCalculator */}
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            mb: 3,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', gap: 1, mr: 2 }}>
              <IconButton
                onClick={onBack}
                sx={{ bgcolor: 'white', boxShadow: 1, '&:hover': { bgcolor: 'grey.100' } }}
              >
                <HomeIcon />
              </IconButton>
              {onBackToDrawing && (
                <IconButton
                  onClick={onBackToDrawing}
                  sx={{ bgcolor: 'white', boxShadow: 1, '&:hover': { bgcolor: 'grey.100' } }}
                >
                  <ArrowForwardIcon />
                </IconButton>
              )}
            </Box>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>Расчёт по характеристикам</Typography>
              <Typography variant="body2" color="text.secondary">
                Коллектор с гидравлическим разделителем
              </Typography>
            </Box>
          </Box>
          {isAdmin && (
            <Tooltip title="Переключение между таблицами для клиентов и инженеров">
              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={(_, val) => { if (val) setViewMode(val); }}
                size="small"
                sx={{ bgcolor: 'white', boxShadow: 1 }}
              >
                <ToggleButton value="clients" sx={{ px: 2, textTransform: 'none', fontSize: '0.8rem' }}>
                  Для клиентов
                </ToggleButton>
                <ToggleButton value="engineers" sx={{ px: 2, textTransform: 'none', fontSize: '0.8rem' }}>
                  Для инженеров
                </ToggleButton>
              </ToggleButtonGroup>
            </Tooltip>
          )}
        </Box>

        <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>Входные характеристики</Typography>
          <Divider sx={{ mb: 3 }} />

          <Grid container spacing={3}>
            {/* Режим ввода — как в распределительном калькуляторе */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>Режим ввода:</Typography>
                <ToggleButtonGroup
                  value={characteristics.inputMode}
                  exclusive
                  onChange={(_, v) => { if (v) updateCharacteristic('inputMode', v); }}
                  size="small"
                >
                  <ToggleButton value="power">По мощности</ToggleButton>
                  <ToggleButton value="flowRate">По расходу</ToggleButton>
                </ToggleButtonGroup>
              </Box>
            </Grid>

            {/* Сечение */}
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Сечение</InputLabel>
                <Select
                  value={characteristics.shape}
                  label="Сечение"
                  onChange={(e) => updateCharacteristic('shape', e.target.value)}
                >
                  <MenuItem value="round">Круглое</MenuItem>
                  <MenuItem value="square">Квадратное</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {viewMode === 'engineers' && (
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth>
                  <InputLabel>Разница температур, °C (ΔT)</InputLabel>
                  <Select
                    value={characteristics.deltaTemp}
                    label="Разница температур, °C (ΔT)"
                    onChange={(e) => updateCharacteristic('deltaTemp', Number(e.target.value))}
                  >
                    {ALLOWED_DELTA_TEMPS.map((v) => (
                      <MenuItem key={v} value={v}>{v}°C</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
            <Grid item xs={12} sm={4}>
              {characteristics.inputMode === 'power' ? (
                <TextField
                  label="Тепловая мощность, кВт"
                  type="number"
                  fullWidth
                  value={characteristics.power === '' ? '' : characteristics.power}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v === '') {
                      setCharacteristics((p) => ({ ...p, power: '' }));
                      return;
                    }
                    const n = parseFloat(v);
                    if (!Number.isNaN(n)) updateCharacteristic('power', n);
                  }}
                  onBlur={(e) => {
                    const n = parseFloat(e.target.value);
                    const maxP = maxPowerKwForInputs;
                    const fallback = Math.max(
                      THERMAL_POWER_INPUT_MIN_KW,
                      Math.min(85, maxP)
                    );
                    if (Number.isNaN(n) || n <= 0) {
                      updateCharacteristic('power', fallback, {
                        finalizeClientPreset: viewMode === 'clients',
                      });
                      setCommittedThermal((prev) => ({ ...(prev ?? {}), power: fallback }));
                    } else {
                      let clamped = Math.min(
                        Math.max(n, THERMAL_POWER_INPUT_MIN_KW),
                        maxP
                      );
                      if (viewMode === 'clients') {
                        clamped = clampCwsClientPowerKw(clamped, maxP);
                      }
                      updateCharacteristic('power', clamped, {
                        finalizeClientPreset: viewMode === 'clients',
                      });
                      setCommittedThermal((prev) => ({ ...(prev ?? {}), power: clamped }));
                    }
                  }}
                  inputProps={{
                    min: THERMAL_POWER_INPUT_MIN_KW,
                    max: maxPowerKwForInputs,
                    step: 1,
                  }}
                  helperText={powerFlowSizingHelperText}
                  error={!hasValidFlowSizing}
                  FormHelperTextProps={{ sx: { m: 0, minHeight: '1.25rem' } }}
                />
              ) : (
                <TextField
                  label="Расход теплоносителя, м³/ч"
                  type="number"
                  fullWidth
                  value={characteristics.flowRate === '' ? '' : characteristics.flowRate}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v === '') {
                      setCharacteristics((p) => ({ ...p, flowRate: '' }));
                      return;
                    }
                    const n = parseFloat(v);
                    if (!Number.isNaN(n)) updateCharacteristic('flowRate', n);
                  }}
                  onBlur={(e) => {
                    const n = parseFloat(e.target.value);
                    const maxQ = maxFlowRateForPowerLimit;
                    if (Number.isNaN(n) || n <= 0) {
                      const qFb = parseFloat(Math.min(1, maxQ).toFixed(2));
                      updateCharacteristic('flowRate', qFb);
                      setCommittedThermal((prev) => ({ ...(prev ?? {}), flowRate: qFb }));
                      return;
                    }
                    const qFinal = parseFloat(Math.min(Math.max(n, 0.1), maxQ).toFixed(2));
                    updateCharacteristic('flowRate', qFinal);
                    setCommittedThermal((prev) => ({ ...(prev ?? {}), flowRate: qFinal }));
                  }}
                  inputProps={{
                    min: 0.1,
                    max: Number.isFinite(maxFlowRateForPowerLimit) ? maxFlowRateForPowerLimit : undefined,
                    step: 0.1,
                  }}
                  helperText={powerFlowSizingHelperText}
                  error={!hasValidFlowSizing}
                  FormHelperTextProps={{ sx: { m: 0, minHeight: '1.25rem' } }}
                />
              )}
            </Grid>
            <Grid item xs={12} sm={4}>
              {characteristics.inputMode === 'power' ? (
                <TextField
                  label="Рассчитанный расход, м³/ч"
                  value={displayFlowRate}
                  fullWidth
                  disabled
                  InputProps={{ readOnly: true }}
                  helperText=" "
                  FormHelperTextProps={{ sx: { m: 0, minHeight: '1.25rem' } }}
                  sx={{ '& .MuiInputBase-input.Mui-disabled': { WebkitTextFillColor: 'rgba(0,0,0,0.87)' } }}
                />
              ) : (
                <TextField
                  label="Рассчитанная мощность, кВт"
                  value={displayPower}
                  fullWidth
                  disabled
                  InputProps={{ readOnly: true }}
                  helperText=" "
                  FormHelperTextProps={{ sx: { m: 0, minHeight: '1.25rem' } }}
                  sx={{ '& .MuiInputBase-input.Mui-disabled': { WebkitTextFillColor: 'rgba(0,0,0,0.87)' } }}
                />
              )}
            </Grid>

            {viewMode === 'clients' && (
              <Grid item xs={12} sm={4} sx={{ display: 'flex', alignItems: 'flex-start' }}>
                <FormControl fullWidth>
                  <InputLabel>Разница температур, °C (ΔT)</InputLabel>
                  <Select
                    value={normalizeClientThermalDeltaTemp(characteristics.deltaTemp)}
                    label="Разница температур, °C (ΔT)"
                    disabled={clientPresetDeltaTempOn}
                    onChange={(e) => updateCharacteristic('deltaTemp', Number(e.target.value))}
                  >
                    {CLIENT_THERMAL_DELTA_TEMP_CHOICES.map((v) => (
                      <MenuItem key={v} value={v}>{v}°C</MenuItem>
                    ))}
                  </Select>
                  <FormHelperText component="div" sx={{ mx: 0, mt: 0.5, minHeight: '1.25rem' }}>
                    <FormControlLabel
                      sx={{ m: 0, alignItems: 'center' }}
                      control={
                        <Checkbox
                          size="small"
                          checked={clientPresetDeltaTempOn}
                          onChange={(e) =>
                            updateCharacteristic('clientPresetDeltaTemp', e.target.checked)
                          }
                        />
                      }
                      label={CLIENT_STANDARD_PRODUCTS_DELTA_TEMP_LABEL}
                    />
                  </FormHelperText>
                </FormControl>
              </Grid>
            )}

            {/* Давление и заглушка — только для инженеров */}
            {viewMode !== 'clients' && (
              <>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth disabled={clientSizingLockedByPreset}>
                    <InputLabel>Давление</InputLabel>
                    <Select
                      value={characteristics.pressure}
                      label="Давление"
                      onChange={(e) => updateCharacteristic('pressure', Number(e.target.value))}
                    >
                      {isSquare ? (
                        <MenuItem key={4} value={4}>4 бар рабочее / 6 бар испытательное</MenuItem>
                      ) : (
                        [
                          <MenuItem
                            key={6}
                            value={6}
                            disabled={roundPipeNoSixEightPressure}
                          >
                            6 бар рабочее / 8 бар испытательное
                            {roundPipeNoSixEightPressure && ' — недоступно для выбранных ⌀'}
                          </MenuItem>,
                          <MenuItem key={8} value={8}>8 бар рабочее / 10 бар испытательное</MenuItem>,
                        ]
                      )}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Заглушка"
                    value={
                      isSquare ? 'Прямая, 4/6 бар' :
                        roundCollectorNoStraightCap ? 'Эллиптическая, 8/10 бар' :
                          Number(characteristics.pressure) >= 8 ? 'Эллиптическая, 8/10 бар' : 'Прямая, 6/8 бар'
                    }
                    fullWidth
                    disabled
                    InputProps={{ readOnly: true }}
                    helperText="От выбранного давления"
                    FormHelperTextProps={{ sx: { m: 0, minHeight: '1.25rem' } }}
                    sx={{ '& .MuiInputBase-input.Mui-disabled': { WebkitTextFillColor: 'rgba(0,0,0,0.87)' } }}
                  />
                </Grid>
              </>
            )}
          </Grid>
        </Paper>

        {/* Блок гидроразделителя */}
        <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Гидравлический разделитель</Typography>
          <Grid container spacing={2}>

            {/* Диаметр разделителя */}
            <Grid item xs={12} sm={viewMode === 'clients' ? 12 : 6}>
              {clientSizingLockedByPreset ? (
                <TextField
                  label={isSquare ? 'Профиль разделителя' : 'Диаметр разделителя'}
                  value={formatClientPresetSeparatorLabel(
                    HYDRAULIC_SEPARATOR_CLIENT_PARAMS_TABLE[clientPresetPowerKey],
                    isSquare
                  )}
                  fullWidth
                  size="small"
                  disabled
                  InputProps={{ readOnly: true }}
                  helperText="По таблице для клиентов"
                  sx={{ '& .MuiInputBase-input.Mui-disabled': { WebkitTextFillColor: 'rgba(0,0,0,0.87)' } }}
                />
              ) : isClientOverTablePower ? (
                <TextField
                  label={isSquare ? 'Профиль разделителя' : 'Диаметр разделителя'}
                  value={formatClientAutoSeparatorLabelFromKey(
                    characteristics.separatorDiameter,
                    isSquare,
                    optimalSepDiameter,
                  )}
                  fullWidth
                  size="small"
                  disabled
                  InputProps={{ readOnly: true }}
                  helperText="Автоподбор: скорость в корпусе ~0,21 м/с"
                  sx={{ '& .MuiInputBase-input.Mui-disabled': { WebkitTextFillColor: 'rgba(0,0,0,0.87)' } }}
                />
              ) : (
                <FormControl fullWidth size="small">
                  <InputLabel>{isSquare ? 'Профиль разделителя' : 'Диаметр разделителя'}</InputLabel>
                  {!isSquare ? (
                    <Select
                      value={characteristics.separatorDiameter ?? (
                        optimalSepDiameter ? `${optimalSepDiameter.outerDiameter}-${optimalSepDiameter.wallThickness}` : ''
                      )}
                      label="Диаметр разделителя"
                      onChange={(e) => updateCharacteristic('separatorDiameter', e.target.value)}
                    >
                      {filteredSepDiameters.map(({ outerDiameter, wallThickness }) => {
                        const key = `${outerDiameter}-${wallThickness}`;
                        return <MenuItem key={key} value={key}>⌀{outerDiameter} ×{wallThickness}</MenuItem>;
                      })}
                      {filteredSepDiameters.length === 0 && <MenuItem value="">Нет подходящих</MenuItem>}
                    </Select>
                  ) : (
                    <Select
                      value={characteristics.separatorDiameter ?? (
                        optimalSepDiameter ? `${optimalSepDiameter.profile}-${optimalSepDiameter.wallThickness}` : ''
                      )}
                      label="Профиль разделителя"
                      onChange={(e) => updateCharacteristic('separatorDiameter', e.target.value)}
                    >
                      {filteredSepProfiles.map(({ profile, wallThickness }) => {
                        const key = `${profile}-${wallThickness}`;
                        return <MenuItem key={key} value={key}>{profile.replace('x', '×')} ×{wallThickness}</MenuItem>;
                      })}
                      {filteredSepProfiles.length === 0 && <MenuItem value="">Нет подходящих</MenuItem>}
                    </Select>
                  )}
                </FormControl>
              )}
            </Grid>

            {viewMode !== 'clients' && (
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Скорость в корпусе, м/с"
                  value={sepCorpusVelocity != null ? sepCorpusVelocity.toFixed(2) : '—'}
                  fullWidth size="small" disabled
                  InputProps={{ readOnly: true }}
                  helperText="В корпусе разделителя (0.09–0.25 м/с)"
                  sx={{ '& .MuiInputBase-input.Mui-disabled': { WebkitTextFillColor: 'rgba(0,0,0,0.87)' } }}
                />
              </Grid>
            )}

            {/* Подключение источника */}
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 1, borderColor: 'divider' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
                  Подключение источника
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel>
                        {viewMode === 'clients' ? 'Подключение источника' : 'Категория'}
                      </InputLabel>
                      <Select
                        value={characteristics.connectionType}
                        label={viewMode === 'clients' ? 'Подключение источника' : 'Категория'}
                        disabled={clientCatalogConnectionPresetOn}
                        onChange={(e) => updateCharacteristic('connectionType', e.target.value)}
                      >
                        {SEP_CONNECTION_TYPES.map(({ id, label: lbl }) => (
                          <MenuItem key={id} value={id}>{lbl}</MenuItem>
                        ))}
                      </Select>
                      {viewMode === 'clients' && (
                        <FormHelperText component="div" sx={{ mx: 0, mt: 0.5, minHeight: '1.25rem' }}>
                          <FormControlLabel
                            sx={{ m: 0, alignItems: 'center' }}
                            control={
                              <Checkbox
                                size="small"
                                checked={clientPresetConnectionTypeOn}
                                onChange={(e) =>
                                  updateCharacteristic('clientPresetConnectionType', e.target.checked)
                                }
                              />
                            }
                            label={CLIENT_STANDARD_PRODUCTS_CONNECTION_LABEL}
                          />
                        </FormHelperText>
                      )}
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    {clientCatalogConnectionPresetOn ? (
                      <TextField
                        label="Размер подключения"
                        value={(() => {
                          const sz = characteristics.connectionSize;
                          if (sz == null) return '—';
                          const found = SEP_CONNECTION_SIZES.find((s) => s.diameter === sz);
                          return found ? found.displayName : `⌀${sz} мм`;
                        })()}
                        fullWidth
                        size="small"
                        disabled
                        InputProps={{ readOnly: true }}
                        helperText="Из строки каталога (по расходу / конструкции)"
                        FormHelperTextProps={{ sx: { m: 0 } }}
                        sx={{ '& .MuiInputBase-input.Mui-disabled': { WebkitTextFillColor: 'rgba(0,0,0,0.87)' } }}
                      />
                    ) : (
                      <FormControl fullWidth size="small" error={!characteristics.connectionSize}>
                        <InputLabel>Размер</InputLabel>
                        <Select
                          value={characteristics.connectionSize ?? ''}
                          label="Размер"
                          onChange={(e) => updateCharacteristic('connectionSize', parseFloat(e.target.value))}
                        >
                          {filteredSepConnectionSizes.map((s) => {
                            const idSize = s.isFlangeSize ? s.nominalDiameter : s.diameter;
                            const v = calculateVelocity(
                              actualFlowRate,
                              getSepTransitionPipeDiameter(characteristics.connectionType, idSize),
                              getSepConnectionWallThickness(characteristics.connectionType)
                            );
                            return (
                              <MenuItem key={s.diameter} value={s.diameter}>
                                {s.displayName}{v > 0 ? ` (${v.toFixed(2)} м/с)` : ''}
                              </MenuItem>
                            );
                          })}
                          {filteredSepConnectionSizes.length === 0 && <MenuItem value="">Нет размеров</MenuItem>}
                        </Select>
                        <FormHelperText>
                          {sepConnectionVelocity != null
                            ? `Скорость: ${sepConnectionVelocity.toFixed(2)} м/с (0.3–1.5)`
                            : 'Нет подходящих размеров'}
                        </FormHelperText>
                      </FormControl>
                    )}
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          </Grid>
        </Paper>

        {/* Блок коллектора */}
        <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Распределительный коллектор</Typography>
          <Grid container spacing={2}>

            {/* Диаметр коллектора */}
            <Grid item xs={12} sm={clientCollectorFieldLocked ? 12 : 6}>
              {clientCollectorFieldLocked ? (
                <FormControl fullWidth size="small">
                  <TextField
                    label={isSquare ? 'Профиль коллектора' : 'Диаметр коллектора'}
                    value={
                      clientCatalogCollectorPresetOn
                        ? formatClientPresetCollectorLabel(
                            DISTRIBUTION_COLLECTOR_CLIENT_PARAMS_TABLE[clientPresetPowerKey],
                            isSquare
                          )
                        : (() => {
                            if (isSquare) {
                              const prof =
                                characteristics.collectorProfile ?? optimalSquareCollectorProfile?.value;
                              if (!prof) return '—';
                              const side = parseInt(prof.split('x')[0], 10);
                              const inner = Number.isFinite(side) ? side - 4 : '—';
                              return `${prof.replace('x', '×')} (внутр. ${inner}×${inner})`;
                            }
                            const d =
                              characteristics.collectorDiameter ?? optimalCollectorDiameter?.value;
                            if (d == null) return '—';
                            const wt =
                              availableCollectorDiameters.find((x) => x.value === d)?.wallThickness ?? 2;
                            const inner = d - 2 * wt;
                            return `⌀${d} (внутр. ⌀${inner})`;
                          })()
                    }
                    fullWidth
                    size="small"
                    disabled
                    InputProps={{ readOnly: true }}
                    helperText={
                      isClientOverTablePower ? 'Автоподбор: скорость в гребёнке ~0,6 м/с' : undefined
                    }
                    sx={{ '& .MuiInputBase-input.Mui-disabled': { WebkitTextFillColor: 'rgba(0,0,0,0.87)' } }}
                  />
                  <FormHelperText component="div" sx={{ mx: 0, mt: 0.5, minHeight: '1.25rem' }}>
                    <FormControlLabel
                      sx={{ m: 0, alignItems: 'center' }}
                      control={
                        <Checkbox
                          size="small"
                          checked={clientPresetCollectorSizeOn}
                          onChange={(e) =>
                            updateCharacteristic('clientPresetCollectorSize', e.target.checked)
                          }
                        />
                      }
                      label={CLIENT_STANDARD_PRODUCTS_CONNECTION_LABEL}
                    />
                  </FormHelperText>
                </FormControl>
              ) : (
                <FormControl fullWidth size="small">
                  <InputLabel>{isSquare ? 'Профиль коллектора' : 'Диаметр коллектора'}</InputLabel>
                  {!isSquare ? (
                    <Select
                      value={characteristics.collectorDiameter ?? optimalCollectorDiameter?.value ?? ''}
                      label="Диаметр коллектора"
                      onChange={(e) => updateCharacteristic('collectorDiameter', Number(e.target.value))}
                    >
                      {filteredCollectorDiameters.map(({ value, wallThickness }) => {
                        const inner = value - 2 * (wallThickness ?? 2);
                        return <MenuItem key={value} value={value}>⌀{value} (внутр. ⌀{inner})</MenuItem>;
                      })}
                      {filteredCollectorDiameters.length === 0 && <MenuItem value="">Нет подходящих</MenuItem>}
                    </Select>
                  ) : (
                    <Select
                      value={
                        characteristics.collectorProfile
                          ?? (optimalSquareCollectorProfile?.value ?? '')
                      }
                      label="Профиль коллектора"
                      onChange={(e) => updateCharacteristic('collectorProfile', e.target.value)}
                    >
                      {filteredSquareCollectorProfiles.map(({ value, label, side }) => {
                        const inner = side - 4;
                        return <MenuItem key={value} value={value}>{label} (внутр. {inner}×{inner})</MenuItem>;
                      })}
                      {filteredSquareCollectorProfiles.length === 0 && (
                        <MenuItem value="">Нет подходящих</MenuItem>
                      )}
                    </Select>
                  )}
                  {viewMode === 'clients' && (
                    <FormHelperText component="div" sx={{ mx: 0, mt: 0.5, minHeight: '1.25rem' }}>
                      <FormControlLabel
                        sx={{ m: 0, alignItems: 'center' }}
                        control={
                          <Checkbox
                            size="small"
                            checked={clientPresetCollectorSizeOn}
                            onChange={(e) =>
                              updateCharacteristic('clientPresetCollectorSize', e.target.checked)
                            }
                          />
                        }
                        label={CLIENT_STANDARD_PRODUCTS_CONNECTION_LABEL}
                      />
                    </FormHelperText>
                  )}
                </FormControl>
              )}
            </Grid>

            {!clientCollectorFieldLocked && (
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Скорость в коллекторе, м/с"
                  value={collectorVelocity != null ? collectorVelocity.toFixed(2) : '—'}
                  fullWidth size="small" disabled
                  InputProps={{ readOnly: true }}
                  helperText="В корпусе гребёнки (0,2–0,8 м/с)"
                  sx={{ '& .MuiInputBase-input.Mui-disabled': { WebkitTextFillColor: 'rgba(0,0,0,0.87)' } }}
                />
              </Grid>
            )}

          </Grid>
        </Paper>

        {/* Контуры */}
        {(() => {
          const END_COLOR = '#7b1fa2';
          const LABEL_COL = 60;
          const ROW_COLOR = { supply: '#1976d2', return: '#d32f2f', rightEnd: END_COLOR };

          const renderChipFull = (circuit, index, row, onRemove) => {
            const fitFull  = fittingsData[circuit.fitting]?.displayName ?? circuit.fitting;
            const catLabel = CIRCUIT_FITTING_CATEGORIES.find(c => c.id === circuit.fittingCategory)?.label ?? circuit.fittingCategory;
            const chipLabel = index != null ? `${index + 1}. ${fitFull}` : fitFull;
            const chipColor = ROW_COLOR[row] ?? '#555';
            return (
              <Tooltip key={circuit.id ?? `${row}-${index}`} title={`${catLabel} / ${fitFull} — нажмите для изменения`}>
                <Chip
                  label={chipLabel}
                  size="small"
                  onClick={(e) => openCircuitPopover(e, row, index)}
                  onDelete={onRemove}
                  deleteIcon={<DeleteIcon sx={{ fontSize: '0.8rem !important' }} />}
                  sx={{
                    cursor: 'pointer', height: 24,
                    bgcolor: `${chipColor}18`, border: `1.5px solid ${chipColor}`,
                    color: chipColor, fontWeight: 600,
                    '& .MuiChip-label': { fontSize: '0.7rem', px: 0.75 },
                    '& .MuiChip-deleteIcon': { ml: 0.25, mr: 0.5, color: chipColor, opacity: 0.7, '&:hover': { opacity: 1 } },
                  }}
                />
              </Tooltip>
            );
          };

          const renderRightEndSlot = () => {
            const endRow = 'rightEnd';
            return (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
                <Typography variant="caption" sx={{ fontSize: '0.6rem', color: END_COLOR, fontWeight: 700, lineHeight: 1 }}>
                  Правый торец
                </Typography>
                {rightEndCircuit
                  ? renderChipFull({ ...rightEndCircuit, id: endRow }, null, endRow, () => setRightEndCircuit(null))
                  : (
                    <Tooltip title="Торцевое подключение к системе (сторона от гидрострелки)">
                      <IconButton size="small"
                        onClick={(e) => {
                          const el = e.currentTarget;
                          const rect = el?.getBoundingClientRect();
                          const anchorPosition = rect
                            ? { top: rect.bottom + window.scrollY, left: rect.left + window.scrollX }
                            : { top: 0, left: 0 };
                          setRightEndCircuit({ fittingCategory: DEFAULT_FITTING_CATEGORY, fitting: DEFAULT_FITTING_ID });
                          setCircuitPopover({ anchorPosition, row: endRow, index: null, step: 'category', category: DEFAULT_FITTING_CATEGORY });
                        }}
                        sx={{
                          width: 28, height: 28,
                          bgcolor: `${END_COLOR}18`,
                          border: `1.5px dashed ${END_COLOR}`,
                          color: END_COLOR,
                          borderRadius: '6px',
                          '&:hover': { bgcolor: `${END_COLOR}30`, borderStyle: 'solid' },
                        }}>
                        <Typography variant="caption" sx={{ fontSize: '0.85rem', lineHeight: 1, fontWeight: 700 }}>→</Typography>
                      </IconButton>
                    </Tooltip>
                  )
                }
              </Box>
            );
          };

          return (
            <Paper elevation={2} sx={{ mb: 3, overflow: 'hidden' }}>
              <Box sx={{ px: 3, pt: 2, pb: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>Контуры</Typography>
              </Box>
              <Divider />
              {[
                { row: 'supply', label: 'Подача',  circuits: supplyCircuits, color: '#1976d2', bg: 'rgba(25,118,210,0.03)' },
                { row: 'return', label: 'Обратка', circuits: returnCircuits, color: '#d32f2f', bg: 'rgba(211,47,47,0.03)' },
              ].map(({ row, label, circuits, color, bg }, rowIdx) => (
                <Box key={row}>
                  {rowIdx === 1 && (
                    <Box sx={{
                      display: 'flex', alignItems: 'center',
                      px: 2.5, py: 0.75, bgcolor: 'grey.50',
                      borderTop: '1px dashed', borderBottom: '1px dashed', borderColor: 'divider',
                    }}>
                      <Box sx={{ width: LABEL_COL + 8 + 32, flexShrink: 0 }} />
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1.5, flex: 1 }}>
                        <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.63rem', fontStyle: 'italic', mr: 'auto' }}>
                          Слева гребёнки стыкуются с разделителем; торец справа — подключение к системе (по желанию)
                        </Typography>
                        {renderRightEndSlot()}
                      </Box>
                    </Box>
                  )}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1.25, px: 2.5, bgcolor: bg }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25, width: LABEL_COL, flexShrink: 0 }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: color }} />
                      <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.7rem', color, lineHeight: 1 }}>
                        {label}
                      </Typography>
                    </Box>
                    <Tooltip title="Добавить контур">
                      <IconButton size="small" onClick={() => addCircuit(row)}
                        sx={{ bgcolor: color, color: 'white', width: 22, height: 22, flexShrink: 0, '&:hover': { opacity: 0.85 } }}>
                        <AddIcon sx={{ fontSize: '0.85rem' }} />
                      </IconButton>
                    </Tooltip>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, flex: 1, ml: 4 }}>
                      {circuits.map((circuit, i) =>
                        renderChipFull(circuit, i, row, () => removeCircuit(row, i))
                      )}
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap', fontSize: '0.7rem' }}>
                      {circuits.length} шт.
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Paper>
          );
        })()}

        {/* Кнопка */}
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Button
            variant="contained"
            size="large"
            startIcon={<PlayArrowIcon />}
            onClick={calculateParameters}
            disabled={!canCalculate}
            sx={{ minWidth: 200, py: 1.5, fontWeight: 700 }}
          >
            Рассчитать параметры
          </Button>
        </Box>
        {!canCalculate && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 1 }}>
            {!effectiveSepOd && !effectiveSepProfile ? 'Нет подходящих диаметров разделителя' :
             !characteristics.connectionSize ? 'Выберите размер подключения разделителя' :
             'Нет подходящих диаметров коллектора'}
          </Typography>
        )}

      </Container>

      {/* Попап выбора фитинга контура */}
      {pop && (
        <Popover
          open
          onClose={closeCircuitPopover}
          anchorReference="anchorPosition"
          anchorPosition={pop.anchorPosition}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          PaperProps={{ sx: { mt: 0.5, minWidth: 220, maxHeight: 320 } }}
        >
          {pop.step === 'category' ? (
            <>
              <Typography variant="caption" sx={{ px: 2, pt: 1.5, pb: 0.5, display: 'block', color: 'text.secondary', fontWeight: 600 }}>
                Категория фитинга
              </Typography>
              <List dense disablePadding>
                {CIRCUIT_FITTING_CATEGORIES.map(({ id, label }) => (
                  <ListItem key={id} disablePadding>
                    <ListItemButton selected={popCur?.fittingCategory === id} onClick={() => selectCategory(id)} sx={{ px: 2, py: 0.6 }}>
                      <ListItemText primary={label} primaryTypographyProps={{ variant: 'body2' }} />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </>
          ) : (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', px: 1, pt: 1 }}>
                <IconButton size="small" onClick={() => setCircuitPopover(prev => ({ ...prev, step: 'category' }))}>
                  <ArrowBackIcon sx={{ fontSize: '1rem' }} />
                </IconButton>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                  {CIRCUIT_FITTING_CATEGORIES.find(c => c.id === pop?.category)?.label}
                </Typography>
              </Box>
              <List dense disablePadding sx={{ overflowY: 'auto', maxHeight: 240 }}>
                {getFittingsByCategory(pop?.category ?? DEFAULT_FITTING_CATEGORY, compatibilityFn).map(({ id, label, shortLabel, isCompatible }) => (
                  <ListItem key={id} disablePadding>
                    <ListItemButton
                      selected={popCur?.fitting === id}
                      onClick={() => isCompatible !== false && selectFitting(id)}
                      disabled={isCompatible === false}
                      sx={{ px: 2, py: 0.4, opacity: isCompatible === false ? 0.4 : 1 }}
                    >
                      <ListItemText
                        primary={shortLabel}
                        secondary={isCompatible === false ? `${label} — увеличьте Ø коллектора` : label}
                        primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                        secondaryTypographyProps={{ variant: 'caption', color: isCompatible === false ? 'error' : 'text.secondary' }}
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </>
          )}
        </Popover>
      )}
    </Box>
  );
}

export default CollectorWithSeparatorCharacteristicsCalculator;
